// src/services/ctiService.js
// Real CTI Analysis Service using NIST NVD and MITRE ATT&CK APIs

class CTIService {
  constructor() {
    // Use backend proxy URLs instead of direct API calls
    this.nistBaseUrl = '/api/nist/search'; // Backend proxy for NIST
    this.euvdBaseUrl = '/api/euvd/search'; // Backend proxy for EUVD
    this.mitreBaseUrl = 'https://attack.mitre.org/api/v2';
    this.mitreStixUrl = 'https://raw.githubusercontent.com/mitre/cti/master/enterprise-attack/enterprise-attack.json';

    // Local MITRE API servers (through backend proxy)
    this.localMitreAttackUrl = '/api/mitre/local-attack';
    this.localMitreAttackAllUrl = '/api/mitre/local-attack-all'; // New: All object types
    this.localMitreAtlasUrl = '/api/mitre/local-atlas';

    // Rate limiting for APIs
    this.requestQueue = [];
    this.isProcessing = false;

    // Asset-keyed cache for vulnerability results (vendor:product:version:source)
    // Fixes path-keyed miss: same Apache on different attack paths now hits.
    this.assetCache = new Map(); // key -> { data, ts }
    this.assetCacheTtlMs = 12 * 60 * 60 * 1000;

    // Cache for MITRE data (refresh every 24 hours)
    this.mitreCache = {
      data: null,
      lastUpdated: null,
      cacheExpiry: 24 * 60 * 60 * 1000 // 24 hours in milliseconds
    };

    // Dictionary of known technologies for intelligent inference
    this.knownTechnologies = {
      // Operating Systems
      'windows': { vendor: 'microsoft', product: 'windows' },
      'windows server': { vendor: 'microsoft', product: 'windows_server' },
      'linux': { vendor: 'linux', product: 'linux_kernel' },
      'ubuntu': { vendor: 'canonical', product: 'ubuntu_linux' },
      'debian': { vendor: 'debian', product: 'debian_linux' },
      'redhat': { vendor: 'redhat', product: 'enterprise_linux' },
      'centos': { vendor: 'centos', product: 'centos' },
      'macos': { vendor: 'apple', product: 'macos' },
      'ios': { vendor: 'apple', product: 'iphone_os' },
      'android': { vendor: 'google', product: 'android' },

      // Web Servers
      'apache': { vendor: 'apache', product: 'http_server' },
      'nginx': { vendor: 'f5', product: 'nginx' },
      'iis': { vendor: 'microsoft', product: 'internet_information_services' },
      'tomcat': { vendor: 'apache', product: 'tomcat' },

      // Databases
      'oracle': { vendor: 'oracle', product: 'database_server' },
      'sql server': { vendor: 'microsoft', product: 'sql_server' },
      'mysql': { vendor: 'oracle', product: 'mysql' },
      'postgresql': { vendor: 'postgresql', product: 'postgresql' },
      'postgres': { vendor: 'postgresql', product: 'postgresql' },
      'mongodb': { vendor: 'mongodb', product: 'mongodb' },
      'redis': { vendor: 'redis', product: 'redis' },
      'sqlite': { vendor: 'sqlite', product: 'sqlite' },

      // Applications/Frameworks
      'wordpress': { vendor: 'wordpress', product: 'wordpress' },
      'drupal': { vendor: 'drupal', product: 'drupal' },
      'joomla': { vendor: 'joomla', product: 'joomla' },
      'node.js': { vendor: 'nodejs', product: 'node.js' },
      'php': { vendor: 'php', product: 'php' },
      'java': { vendor: 'oracle', product: 'jre' },
      'python': { vendor: 'python', product: 'python' },
      'docker': { vendor: 'docker', product: 'docker' },
      'kubernetes': { vendor: 'kubernetes', product: 'kubernetes' },

      // Browsers/Clients
      'chrome': { vendor: 'google', product: 'chrome' },
      'firefox': { vendor: 'mozilla', product: 'firefox' },
      'edge': { vendor: 'microsoft', product: 'edge' },
      'office': { vendor: 'microsoft', product: 'office' },
      'outlook': { vendor: 'microsoft', product: 'outlook' }
    };
  }

  // Asset-keyed cache helpers (frontend mirror of backend ctiCacheService)
  buildAssetCacheKey(asset, source) {
    const norm = (s) => String(s || '').toLowerCase().trim().replace(/\s+/g, '_').replace(/[^a-z0-9_.-]/g, '') || '_';
    const v = norm(asset.vendor);
    const p = norm(asset.product);
    const ver = norm(asset.version) || '*';
    const src = norm(source) || 'any';
    return `${v}:${p}:${ver}:${src}`;
  }

  getAssetCached(asset, source) {
    const key = this.buildAssetCacheKey(asset, source);
    const entry = this.assetCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.ts > this.assetCacheTtlMs) {
      this.assetCache.delete(key);
      return null;
    }
    return entry.data;
  }

  setAssetCached(asset, source, data) {
    if (this.assetCache.size >= 200) {
      const oldest = this.assetCache.keys().next().value;
      this.assetCache.delete(oldest);
    }
    const key = this.buildAssetCacheKey(asset, source);
    this.assetCache.set(key, { data, ts: Date.now() });
  }

  // Helper to infer vendor/product from asset name
  inferProductDetails(assetName) {
    if (!assetName) return null;

    const lowerName = assetName.toLowerCase();

    // Check against known technologies
    for (const [key, value] of Object.entries(this.knownTechnologies)) {
      if (lowerName.includes(key)) {
        return { ...value, inferred: true, sourceTerm: key };
      }
    }

    return null;
  }

  // Generate CPE (Common Platform Enumeration) string from asset info
  generateCPE(asset) {
    if (asset.vendor && asset.product) {
      // Format: cpe:2.3:a:vendor:product:version:*:*:*:*:*:*:*
      const vendor = asset.vendor.toLowerCase().replace(/\s+/g, '_');
      const product = asset.product.toLowerCase().replace(/\s+/g, '_');
      const version = asset.version || '*';
      return `cpe:2.3:a:${vendor}:${product}:${version}:*:*:*:*:*:*:*`;
    }
    return null;
  }

  // Search for vulnerabilities using NIST or EUVD API based on analysis type
  async searchVulnerabilities(asset, analysisType = 'euvd') {
    try {
      console.log(`[CTI] Searching vulnerabilities for asset: ${asset.name} using ${analysisType}`);

      // Route to appropriate API based on analysis type
      if (analysisType === 'nist' || analysisType === 'combined') {
        return await this.searchNISTVulnerabilities(asset);
      } else if (analysisType === 'euvd') {
        return await this.searchEUVDVulnerabilities(asset);
      }

      return {
        vulnerabilities: [],
        searchMethod: 'unsupported',
        searchSuccess: false
      };

    } catch (error) {
      console.error(`[CTI] Error searching vulnerabilities for ${asset.name}:`, error);
      return {
        vulnerabilities: [],
        searchMethod: 'error',
        searchSuccess: false
      };
    }
  }

  // Search for vulnerabilities using NIST NVD API with CPE-first strategy
  async searchNISTVulnerabilities(asset) {
    try {
      console.log(`[CTI] NIST search for asset: ${asset.name}`);

      let allVulnerabilities = [];
      let searchMethod = 'none';

      // Try to infer vendor/product if missing
      let effectiveVendor = asset.vendor;
      let effectiveProduct = asset.product;
      let effectiveVersion = asset.version;

      if (!effectiveVendor || !effectiveProduct) {
        const inferred = this.inferProductDetails(asset.name);
        if (inferred) {
          console.log(`[CTI] Inferred details for "${asset.name}": Vendor=${inferred.vendor}, Product=${inferred.product}`);
          if (!effectiveVendor) effectiveVendor = inferred.vendor;
          if (!effectiveProduct) effectiveProduct = inferred.product;
        }
      }

      // ========================================
      // STRATEGY 1: CPE Search (Primary - Most Precise)
      // ========================================
      if (effectiveVendor && effectiveProduct) {
        // Generate CPE string for the asset
        const cpe = this.generateCPEFromDetails(effectiveVendor, effectiveProduct, effectiveVersion);
        console.log(`[CTI] Strategy 1: NIST CPE search with: ${cpe}`);

        const cpeResults = await this.searchByNISTCPE(asset, cpe);
        if (cpeResults.length > 0) {
          allVulnerabilities = cpeResults;
          searchMethod = 'NIST CPE';
          console.log(`[CTI] ✅ NIST CPE search successful: ${cpeResults.length} vulnerabilities found`);
        } else {
          console.log(`[CTI] NIST CPE search yielded no results, falling back to keyword search...`);
        }
      } else {
        console.log(`[CTI] Cannot generate CPE - missing vendor/product for asset: ${asset.name}`);
      }

      // ========================================
      // STRATEGY 2: Vendor + Product Keyword Search (Fallback)
      // ========================================
      if (allVulnerabilities.length === 0) {
        let vendorProductKeyword = '';

        if (effectiveVendor && effectiveProduct) {
          vendorProductKeyword = `${effectiveVendor} ${effectiveProduct}`;
        } else if (effectiveProduct) {
          vendorProductKeyword = effectiveProduct;
        } else if (effectiveVendor) {
          vendorProductKeyword = effectiveVendor;
        }

        if (vendorProductKeyword) {
          console.log(`[CTI] Strategy 2: NIST vendor+product keyword search with: ${vendorProductKeyword}`);
          const vendorProductResults = await this.searchByNISTKeywords(asset, vendorProductKeyword);
          if (vendorProductResults.length > 0) {
            allVulnerabilities = vendorProductResults;
            searchMethod = 'NIST Keyword (Vendor+Product)';
            console.log(`[CTI] ✅ NIST vendor+product keyword search successful: ${vendorProductResults.length} vulnerabilities found`);
          } else {
            console.log(`[CTI] NIST vendor+product keyword search yielded no results, trying broader keywords...`);
          }
        }
      }

      // ========================================
      // STRATEGY 3: Broader Keyword Search (Last Resort)
      // ========================================
      if (allVulnerabilities.length === 0) {
        const keywords = this.generateKeywords(asset);
        if (keywords.length > 0) {
          console.log(`[CTI] Strategy 3: NIST broad keyword search with: ${keywords.join(', ')}`);

          // Try different keyword combinations
          for (const keywordSet of keywords) {
            const keywordResults = await this.searchByNISTKeywords(asset, keywordSet);
            if (keywordResults.length > 0) {
              allVulnerabilities = [...allVulnerabilities, ...keywordResults];
              searchMethod = 'NIST Keyword (Broad)';
              console.log(`[CTI] ✅ NIST broad keyword search "${keywordSet}" found ${keywordResults.length} vulnerabilities`);
              break; // Use first successful keyword search
            }
          }
        }
      }

      // Remove duplicates based on CVE ID
      const uniqueVulnerabilities = this.deduplicateVulnerabilities(allVulnerabilities);

      console.log(`[CTI] NIST final results for ${asset.name}: ${uniqueVulnerabilities.length} unique vulnerabilities (Method: ${searchMethod})`);
      return {
        vulnerabilities: uniqueVulnerabilities,
        searchMethod: searchMethod,
        searchSuccess: uniqueVulnerabilities.length > 0
      };

    } catch (error) {
      console.error(`[CTI] Error in NIST search for ${asset.name}:`, error);
      return {
        vulnerabilities: [],
        searchMethod: 'error',
        searchSuccess: false
      };
    }
  }

  // Generate CPE string from vendor, product, and version details
  generateCPEFromDetails(vendor, product, version) {
    // Format: cpe:2.3:a:vendor:product:version:*:*:*:*:*:*:*
    // CPE requires lowercase and underscores for spaces
    const v = vendor.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_.-]/g, '');
    const p = product.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_.-]/g, '');
    const ver = version ? version.toLowerCase().replace(/\s+/g, '_') : '*';

    return `cpe:2.3:a:${v}:${p}:${ver}:*:*:*:*:*:*:*`;
  }

  // Search for vulnerabilities using EUVD API with keyword search
  async searchEUVDVulnerabilities(asset) {
    try {
      console.log(`[CTI] EUVD search for asset: ${asset.name}`);

      let allVulnerabilities = [];
      let searchMethod = 'none';

      // Strategy 1: EUVD vendor + product search (primary method)
      // Try to infer vendor/product if missing
      let effectiveVendor = asset.vendor;
      let effectiveProduct = asset.product;

      if (!effectiveVendor || !effectiveProduct) {
        const inferred = this.inferProductDetails(asset.name);
        if (inferred) {
          console.log(`[CTI] Inferred details for "${asset.name}": Vendor=${inferred.vendor}, Product=${inferred.product}`);
          if (!effectiveVendor) effectiveVendor = inferred.vendor;
          if (!effectiveProduct) effectiveProduct = inferred.product;
        }
      }

      if (effectiveVendor && effectiveProduct) {
        console.log(`[CTI] Strategy 1: EUVD vendor+product search with: ${effectiveVendor} ${effectiveProduct}`);
        // Create a temporary asset object with inferred details for the search
        const searchAsset = { ...asset, vendor: effectiveVendor, product: effectiveProduct };
        const vendorProductResults = await this.searchByEUVDVendorProduct(searchAsset);
        if (vendorProductResults.length > 0) {
          allVulnerabilities = vendorProductResults;
          searchMethod = 'EUVD Vendor+Product';
          console.log(`[CTI] EUVD vendor+product search successful: ${vendorProductResults.length} vulnerabilities found`);
        } else {
          console.log(`[CTI] EUVD vendor+product search yielded no results, trying text keyword search...`);
        }
      } else {
        console.log(`[CTI] Missing vendor/product for asset: ${asset.name}, using text keyword search`);
      }

      // Strategy 2: EUVD text keyword search (fallback)
      if (allVulnerabilities.length === 0) {
        const keywords = this.generateKeywords(asset);
        if (keywords.length > 0) {
          console.log(`[CTI] Strategy 2: EUVD text keyword search with: ${keywords.join(', ')}`);

          // Try different keyword combinations
          for (const keywordSet of keywords) {
            const keywordResults = await this.searchByEUVDText(asset, keywordSet);
            if (keywordResults.length > 0) {
              allVulnerabilities = [...allVulnerabilities, ...keywordResults];
              searchMethod = searchMethod === 'none' ? 'EUVD Text' : `${searchMethod} + Text`;
              console.log(`[CTI] EUVD text search "${keywordSet}" found ${keywordResults.length} vulnerabilities`);
              break; // Use first successful keyword search
            }
          }
        }
      }

      // Remove duplicates based on CVE ID
      const uniqueVulnerabilities = this.deduplicateVulnerabilities(allVulnerabilities);

      console.log(`[CTI] EUVD final results for ${asset.name}: ${uniqueVulnerabilities.length} unique vulnerabilities (Method: ${searchMethod})`);
      return {
        vulnerabilities: uniqueVulnerabilities,
        searchMethod: searchMethod,
        searchSuccess: uniqueVulnerabilities.length > 0
      };

    } catch (error) {
      console.error(`[CTI] Error in EUVD search for ${asset.name}:`, error);
      return {
        vulnerabilities: [],
        searchMethod: 'error',
        searchSuccess: false
      };
    }
  }

  // EUVD vendor + product search method
  async searchByEUVDVendorProduct(asset) {
    try {
      const params = new URLSearchParams({
        vendor: asset.vendor,
        product: asset.product,
        size: '20', // Max 100, using 20 for reasonable response time
        page: '0'
      });

      console.log(`[CTI] EUVD vendor+product search URL: ${this.euvdBaseUrl}?${params}`);

      const response = await fetch(`${this.euvdBaseUrl}?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error(`[CTI] EUVD vendor+product API error: ${response.status} ${response.statusText}`);
        return [];
      }

      const data = await response.json();
      console.log(`[CTI] EUVD vendor+product response for "${asset.vendor} ${asset.product}": ${data.totalElements || data.length || 0} vulnerabilities`);
      return this.parseEUVDVulnerabilities(data);
    } catch (error) {
      console.error(`[CTI] EUVD vendor+product search failed for ${asset.name}:`, error);
      return [];
    }
  }

  // EUVD text keyword search method
  async searchByEUVDText(asset, keywords) {
    try {
      const params = new URLSearchParams({
        text: keywords, // EUVD text parameter for keyword search
        size: '15', // Reasonable size for keyword search
        page: '0'
      });

      console.log(`[CTI] EUVD text search URL: ${this.euvdBaseUrl}?${params}`);

      const response = await fetch(`${this.euvdBaseUrl}?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error(`[CTI] EUVD text API error: ${response.status} ${response.statusText}`);
        return [];
      }

      const data = await response.json();
      console.log(`[CTI] EUVD text search response for "${keywords}": ${data.totalElements || data.length || 0} vulnerabilities`);
      return this.parseEUVDVulnerabilities(data);
    } catch (error) {
      console.error(`[CTI] EUVD text search failed for ${asset.name} with keywords "${keywords}":`, error);
      return [];
    }
  }

  // NIST CPE-based search method (Primary search strategy per NIST best practices)
  async searchByNISTCPE(asset, cpe) {
    try {
      console.log(`[CTI] NIST CPE search URL: /api/nist/cpe-search?cpeName=${encodeURIComponent(cpe)}`);

      // For CPE search, we use the cpeName parameter on the CVE endpoint
      const params = new URLSearchParams({
        cpeName: cpe,
        resultsPerPage: '20',
        startIndex: '0'
      });

      const response = await fetch(`/api/nist/cpe-search?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error(`[CTI] NIST CPE API error: ${response.status} ${response.statusText}`);
        return [];
      }

      const data = await response.json();
      console.log(`[CTI] NIST CPE search response for "${cpe}": ${data.totalResults || 0} total, ${data.vulnerabilities?.length || 0} returned`);

      return this.parseNISTVulnerabilities(data);
    } catch (error) {
      console.error(`[CTI] NIST CPE search failed for ${asset.name}:`, error);
      return [];
    }
  }

  // NIST keyword-based search method
  async searchByNISTKeywords(asset, keywords) {
    try {
      const params = new URLSearchParams({
        keywordSearch: keywords, // NIST keywordSearch parameter
        resultsPerPage: '15',
        startIndex: '0'
      });

      console.log(`[CTI] NIST keyword search URL: ${this.nistBaseUrl}?${params}`);

      const response = await fetch(`${this.nistBaseUrl}?${params}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        console.error(`[CTI] NIST keyword API error: ${response.status} ${response.statusText}`);
        return [];
      }

      const data = await response.json();
      console.log(`[CTI] NIST keyword search response for "${keywords}": ${data.totalResults || 0} total, ${data.vulnerabilities?.length || 0} returned`);
      return this.parseNISTVulnerabilities(data);
    } catch (error) {
      console.error(`[CTI] NIST keyword search failed for ${asset.name} with keywords "${keywords}":`, error);
      return [];
    }
  }

  // Generate keyword search terms from asset information based on product names
  generateKeywords(asset) {
    const keywords = [];

    // Strategy 1: Vendor + Product (most specific)
    if (asset.vendor && asset.product) {
      keywords.push(`${asset.vendor} ${asset.product}`);
    }

    // Strategy 2: Product only (broader search)
    if (asset.product) {
      keywords.push(asset.product);
    }

    // Strategy 3: Vendor only (very broad)
    if (asset.vendor) {
      keywords.push(asset.vendor);
    }

    // Strategy 4: Extract product names from asset name using enhanced patterns
    if (asset.name) {
      const productKeywords = this.extractProductKeywordsForVulnSearch(asset);
      keywords.push(...productKeywords);

      // Also add inferred product if available
      const inferred = this.inferProductDetails(asset.name);
      if (inferred) {
        keywords.push(inferred.product);
        keywords.push(`${inferred.vendor} ${inferred.product}`);
      }
    }

    // Strategy 5: Common software terms for email servers
    if (asset.name && (asset.name.toLowerCase().includes('email') || asset.name.toLowerCase().includes('mail'))) {
      const emailKeywords = ['postfix', 'sendmail', 'exim', 'qmail', 'dovecot', 'exchange'];
      emailKeywords.forEach(keyword => {
        if (!keywords.includes(keyword)) {
          keywords.push(keyword);
        }
      });
    }

    // Remove duplicates and empty strings
    const uniqueKeywords = [...new Set(keywords)].filter(k => k && k.trim().length > 0);

    console.log(`[CTI] Generated vulnerability search keywords for ${asset.name}:`, uniqueKeywords);
    return uniqueKeywords;
  }

  // Extract product keywords specifically for vulnerability searches - simple approach
  extractProductKeywordsForVulnSearch(asset) {
    const keywords = [];

    if (!asset) return keywords;

    // Use the product name from the business asset
    if (asset.product) {
      keywords.push(asset.product);
      console.log(`[CTI] Using product name for vulnerability search: '${asset.product}'`);
    } else if (asset.name) {
      // Fallback to asset name if no product specified
      keywords.push(asset.name);
      console.log(`[CTI] No product specified, using asset name for vulnerability search: '${asset.name}'`);
    }

    return keywords;
  }

  // Parse vulnerabilities from EUVD API response
  parseEUVDVulnerabilities(data) {
    console.log(`[CTI] Parsing EUVD response structure:`, {
      isArray: Array.isArray(data),
      hasItems: !!data.items,
      hasContent: !!data.content,
      hasElements: !!data.elements,
      totalElements: data.totalElements,
      dataKeys: Object.keys(data || {})
    });

    // EUVD returns either an array directly or an object with content/elements/items
    const vulnerabilities = data.items || data.content || data.elements || (Array.isArray(data) ? data : []);

    console.log(`[CTI] Extracted vulnerabilities array length: ${vulnerabilities.length}`);

    // Log first vulnerability structure for debugging
    if (vulnerabilities.length > 0) {
      console.log(`[CTI] First vulnerability structure:`, {
        id: vulnerabilities[0].id,
        cveId: vulnerabilities[0].cveId,
        references: vulnerabilities[0].references,
        referencesType: typeof vulnerabilities[0].references,
        aliases: vulnerabilities[0].aliases,
        assigner: vulnerabilities[0].assigner,
        epss: vulnerabilities[0].epss,
        epssScore: vulnerabilities[0].epssScore,
        baseScore: vulnerabilities[0].baseScore,
        baseScoreVector: vulnerabilities[0].baseScoreVector
      });
    }

    if (vulnerabilities && vulnerabilities.length > 0) {
      const parsed = vulnerabilities.map(vuln => {
        // Ensure references is always an array - PRESERVE ALL REFERENCES FOR SAVE
        let references = [];
        console.log(`[CTI] Processing references for ${vuln.id || 'unknown'}:`, {
          hasReferences: !!vuln.references,
          referencesType: typeof vuln.references,
          referencesValue: vuln.references,
          referencesLength: vuln.references?.length
        });

        if (vuln.references) {
          if (Array.isArray(vuln.references)) {
            references = vuln.references; // Keep all references for complete data
            console.log(`[CTI] References are array with ${references.length} items`);
          } else if (typeof vuln.references === 'string') {
            // EUVD references are newline-separated strings
            console.log(`[CTI] References are string: "${vuln.references}"`);
            const refUrls = vuln.references.split('\n').filter(url => url.trim());
            console.log(`[CTI] Split references into ${refUrls.length} URLs:`, refUrls);
            references = refUrls.map((url, index) => ({
              url: url.trim(),
              name: `Reference ${index + 1}`,
              source: 'EUVD'
            }));
          } else if (typeof vuln.references === 'object') {
            // If references is an object, wrap it in an array
            references = [vuln.references];
            console.log(`[CTI] References are object, wrapped in array`);
          }
        } else {
          console.log(`[CTI] No references found for ${vuln.id || 'unknown'}`);
        }

        console.log(`[CTI] Final processed references for ${vuln.id || 'unknown'}:`, references);

        return {
          id: vuln.cveId || vuln.id,
          description: vuln.description || vuln.summary || 'No description available',
          severity: this.extractEUVDSeverity(vuln),
          publishedDate: vuln.publishedDate || vuln.datePublished,
          lastModified: vuln.lastModifiedDate || vuln.dateModified || vuln.dateUpdated,
          references: references,
          // EUVD specific fields - Enhanced mapping based on actual response structure
          cvssScore: vuln.cvssScore || vuln.baseScore || vuln.score,
          baseScore: vuln.baseScore || vuln.cvssScore || vuln.score, // Alternative field name
          score: vuln.baseScore || vuln.cvssScore || vuln.score,     // Another alternative
          cvssVector: vuln.baseScoreVector || vuln.cvssVector || vuln.vector,
          vector: vuln.baseScoreVector || vuln.cvssVector || vuln.vector,
          epssScore: vuln.epss || vuln.epssScore, // EUVD uses 'epss' field
          exploited: vuln.exploited,
          assigner: vuln.assigner,
          aliases: vuln.aliases, // CVE aliases (newline-separated string)
          vendor: vuln.vendor,
          product: vuln.product,
          // Additional EUVD fields from the example response
          baseScoreVersion: vuln.baseScoreVersion,
          enisaIdProduct: vuln.enisaIdProduct,
          enisaIdVendor: vuln.enisaIdVendor,
          source: 'EUVD' // Mark as EUVD source
        };
      });

      console.log(`[CTI] Parsed ${parsed.length} vulnerabilities:`, parsed);
      return parsed;
    }

    console.log(`[CTI] No vulnerabilities found in response`);
    return [];
  }

  // Extract severity from EUVD vulnerability data
  extractEUVDSeverity(vuln) {
    const score = vuln.cvssScore || vuln.baseScore || vuln.score || 0;
    let severity = 'UNKNOWN';

    // Map CVSS score to severity
    if (score >= 9.0) severity = 'CRITICAL';
    else if (score >= 7.0) severity = 'HIGH';
    else if (score >= 4.0) severity = 'MEDIUM';
    else if (score > 0) severity = 'LOW';

    console.log(`[CTI] Extracted severity for ${vuln.id || 'unknown'}: score=${score}, severity=${severity}`);

    // Return both nested object AND flattened fields for MongoDB compatibility
    return {
      score: score,
      severity: severity,
      vector: vuln.cvssVector || vuln.vector || '',
      // Flattened fields for backend compatibility
      severityScore: score,
      severityLevel: severity,
      severityVector: vuln.cvssVector || vuln.vector || ''
    };
  }

  // Parse vulnerabilities from NIST API response
  parseNISTVulnerabilities(data) {
    if (data.vulnerabilities && data.vulnerabilities.length > 0) {
      return data.vulnerabilities.map(vuln => {
        // Extract CWE weaknesses for CWE→ATT&CK mapping (Slice 3)
        const weaknesses = (vuln.cve.weaknesses || [])
          .flatMap((w) => w.description || [])
          .map((d) => d.value)
          .filter((v) => v && String(v).toUpperCase().startsWith('CWE-'));
        return {
          id: vuln.cve.id,
          description: vuln.cve.descriptions.find(d => d.lang === 'en')?.value || 'No description',
          severity: this.extractSeverity(vuln.cve.metrics),
          publishedDate: vuln.cve.published,
          lastModified: vuln.cve.lastModified,
          references: vuln.cve.references?.slice(0, 3) || [],
          weaknesses,
          cweIds: weaknesses
        };
      });
    }
    return [];
  }

  // CWE → ATT&CK mapping (Slice 3) - inline to avoid bundler path issues
  getCweAttckMap() {
    if (this.cweAttckMap) return this.cweAttckMap;
    this.cweAttckMap = {
      'CWE-79': ['T1059.007', 'T1189', 'T1190'],
      'CWE-89': ['T1190', 'T1059.007', 'T1505.003'],
      'CWE-20': ['T1190', 'T1059.007'],
      'CWE-22': ['T1083', 'T1005', 'T1552.001'],
      'CWE-78': ['T1059.001', 'T1059.003', 'T1190'],
      'CWE-94': ['T1059.007', 'T1059.001', 'T1203'],
      'CWE-287': ['T1078', 'T1110.001', 'T1552'],
      'CWE-200': ['T1005', 'T1083', 'T1552.001'],
      'CWE-352': ['T1566.002', 'T1204.001'],
      'CWE-434': ['T1204.002', 'T1059.007'],
      'CWE-502': ['T1059.007', 'T1190'],
      'CWE-611': ['T1005', 'T1083'],
      'CWE-862': ['T1078', 'T1548'],
      'CWE-863': ['T1078', 'T1548.002'],
      'CWE-798': ['T1078.001', 'T1552.001', 'T1110'],
      'CWE-119': ['T1059', 'T1203', 'T1068'],
      'CWE-120': ['T1059', 'T1203'],
      'CWE-121': ['T1059', 'T1203'],
      'CWE-416': ['T1059', 'T1068'],
      'CWE-918': ['T1090', 'T1190', 'T1071'],
      'CWE-77': ['T1059.001', 'T1059.003'],
      'CWE-269': ['T1068', 'T1548.002', 'T1078'],
      'CWE-307': ['T1110.001', 'T1110'],
      'CWE-400': ['T1496', 'T1499'],
      'CWE-601': ['T1204.001', 'T1566.002']
    };
    return this.cweAttckMap;
  }

  // Derive ATT&CK technique IDs from CVE weaknesses
  deriveTechniqueIdsFromVulns(vulnerabilities) {
    const map = this.getCweAttckMap();
    const ids = new Set();
    (vulnerabilities || []).forEach((v) => {
      const cwes = v.cweIds || v.weaknesses || [];
      cwes.forEach((cwe) => {
        const key = String(cwe).toUpperCase().trim();
        const normalized = key.startsWith('CWE-') ? key : `CWE-${key}`;
        const techniques = map[normalized] || map[key] || [];
        techniques.forEach((t) => ids.add(t));
      });
      // Fallback: infer from description keywords if no CWE
      if ((!cwes || cwes.length === 0) && v.description) {
        const desc = v.description.toLowerCase();
        if (desc.includes('sql injection')) map['CWE-89']?.forEach((t) => ids.add(t));
        else if (desc.includes('cross-site scripting') || desc.includes('xss')) map['CWE-79']?.forEach((t) => ids.add(t));
        else if (desc.includes('remote code execution') || desc.includes(' rce ')) map['CWE-94']?.forEach((t) => ids.add(t));
      }
    });
    return Array.from(ids);
  }

  // Fetch MITRE techniques by explicit IDs via local ATT&CK proxy
  async fetchTechniquesByIds(techniqueIds) {
    if (!techniqueIds || techniqueIds.length === 0) return [];
    const results = [];
    // Query local ATT&CK for each ID (ATT&CK search by q=id returns it)
    for (const tid of techniqueIds.slice(0, 10)) {
      const base = tid.split('.')[0]; // T1059.007 -> T1059
      const q = tid;
      const params = new URLSearchParams({ q, type: 'attack-pattern', isSubtechnique: tid.includes('.') ? 'true' : 'false' });
      try {
        const res = await fetch(`${this.localMitreAttackUrl}?${params}`);
        if (!res.ok) continue;
        const body = await res.json();
        const arr = body.success ? body.data : body;
        if (Array.isArray(arr) && arr.length > 0) {
          // Prefer exact ID match
          const exact = arr.find((t) => {
            const eid = t.external_references?.find((r) => r.source_name === 'mitre-attack')?.external_id;
            return eid === tid;
          }) || arr[0];
          results.push(this.parseLocalMitreTechnique(exact));
        } else if (tid.includes('.')) {
          // Try base technique if sub-technique not found
          const p2 = new URLSearchParams({ q: base, type: 'attack-pattern', isSubtechnique: 'false' });
          const r2 = await fetch(`${this.localMitreAttackUrl}?${p2}`).then((r) => r.json()).catch(() => null);
          const a2 = r2?.success ? r2.data : r2;
          if (Array.isArray(a2) && a2.length > 0) results.push(this.parseLocalMitreTechnique(a2[0]));
        }
      } catch (_) {}
    }
    // Dedup by id
    const uniq = [];
    const seen = new Set();
    results.forEach((t) => { if (!seen.has(t.id)) { seen.add(t.id); uniq.push(t); } });
    return uniq;
  }

  // Dedup with merge: same CVE ID → keep max CVSS, union refs, both sources
  deduplicateVulnerabilities(vulnerabilities) {
    const map = new Map(); // id -> vuln
    vulnerabilities.forEach((vuln) => {
      if (!vuln || !vuln.id) return;
      const id = String(vuln.id).toUpperCase();
      const existing = map.get(id);
      if (!existing) {
        map.set(id, { ...vuln, id });
        return;
      }
      // Merge: max CVSS
      const scoreA = existing.severity?.score ?? existing.cvssScore ?? existing.score ?? 0;
      const scoreB = vuln.severity?.score ?? vuln.cvssScore ?? vuln.score ?? 0;
      const bestScore = Math.max(Number(scoreA) || 0, Number(scoreB) || 0);
      // Prefer entry with higher score for base fields, but keep both sources
      const base = (Number(scoreB) || 0) > (Number(scoreA) || 0) ? vuln : existing;
      // Union refs
      const refsA = Array.isArray(existing.references) ? existing.references : existing.references ? [existing.references] : [];
      const refsB = Array.isArray(vuln.references) ? vuln.references : vuln.references ? [vuln.references] : [];
      const union = [];
      const seenUrl = new Set();
      [...refsA, ...refsB].forEach((r) => {
        const url = typeof r === 'string' ? r : r?.url || '';
        const key = url || JSON.stringify(r);
        if (!seenUrl.has(key)) { seenUrl.add(key); union.push(r); }
      });
      const sources = new Set([existing.source, vuln.source].filter(Boolean));
      const merged = {
        ...base,
        id,
        cvssScore: bestScore,
        score: bestScore,
        baseScore: bestScore,
        severity: { ...(base.severity || {}), score: bestScore, severityScore: bestScore },
        references: union,
        source: sources.size > 1 ? Array.from(sources).join('+') : base.source || existing.source,
        _dedupSources: Array.from(sources),
      };
      // Ensure severity text matches best score if needed
      if (!merged.severity.severity || merged.severity.severity === 'UNKNOWN') {
        if (bestScore >= 9) merged.severity.severity = 'CRITICAL';
        else if (bestScore >= 7) merged.severity.severity = 'HIGH';
        else if (bestScore >= 4) merged.severity.severity = 'MEDIUM';
        else if (bestScore > 0) merged.severity.severity = 'LOW';
      }
      map.set(id, merged);
    });
    return Array.from(map.values());
  }

  // Extract severity from CVSS metrics
  extractSeverity(metrics) {
    if (metrics?.cvssMetricV31 && metrics.cvssMetricV31.length > 0) {
      return {
        score: metrics.cvssMetricV31[0].cvssData.baseScore,
        severity: metrics.cvssMetricV31[0].cvssData.baseSeverity,
        vector: metrics.cvssMetricV31[0].cvssData.vectorString
      };
    }
    if (metrics?.cvssMetricV30 && metrics.cvssMetricV30.length > 0) {
      return {
        score: metrics.cvssMetricV30[0].cvssData.baseScore,
        severity: metrics.cvssMetricV30[0].cvssData.baseSeverity,
        vector: metrics.cvssMetricV30[0].cvssData.vectorString
      };
    }
    if (metrics?.cvssMetricV2 && metrics.cvssMetricV2.length > 0) {
      return {
        score: metrics.cvssMetricV2[0].cvssData.baseScore,
        severity: this.mapV2Severity(metrics.cvssMetricV2[0].cvssData.baseScore),
        vector: metrics.cvssMetricV2[0].cvssData.vectorString
      };
    }
    return { score: 0, severity: 'UNKNOWN', vector: '' };
  }

  // Map CVSS v2 score to severity
  mapV2Severity(score) {
    if (score >= 9.0) return 'CRITICAL';
    if (score >= 7.0) return 'HIGH';
    if (score >= 4.0) return 'MEDIUM';
    return 'LOW';
  }

  // --- Slice 2: KEV + EPSS enrichment + ranking ---

  async enrichWithKevEpss(vulnerabilities) {
    if (!vulnerabilities || vulnerabilities.length === 0) return vulnerabilities;
    const ids = vulnerabilities.map((v) => String(v.id || '').toUpperCase()).filter(Boolean);
    if (ids.length === 0) return vulnerabilities;
    let kevSet = new Set();
    let epssMap = {};
    try {
      const [kevRes, epssRes] = await Promise.allSettled([
        fetch(`/api/enrich/kev/check?cves=${encodeURIComponent(ids.slice(0, 100).join(','))}`).then((r) => r.ok ? r.json() : null),
        fetch(`/api/enrich/epss?cves=${encodeURIComponent(ids.slice(0, 100).join(','))}`).then((r) => r.ok ? r.json() : null),
      ]);
      if (kevRes.status === 'fulfilled' && kevRes.value?.result) {
        Object.entries(kevRes.value.result).forEach(([k, v]) => { if (v) kevSet.add(k); });
      }
      if (epssRes.status === 'fulfilled' && epssRes.value?.result) {
        epssMap = epssRes.value.result;
      }
    } catch (_) {}
    return vulnerabilities.map((v) => {
      const id = String(v.id || '').toUpperCase();
      const epssEntry = epssMap[id];
      const epss = epssEntry?.epss ?? v.epssScore ?? v.epss ?? null;
      const kev = kevSet.has(id);
      const euvdExploited = !!v.exploited;
      return { ...v, kev, euvdExploited, epss, epssScore: epss, _enriched: true };
    });
  }

  rankVulnerabilities(vulnerabilities) {
    const now = Date.now();
    const scored = vulnerabilities.map((v) => {
      const kev = !!v.kev;
      const euvdExploited = !!v.euvdExploited || !!v.exploited;
      const epss = typeof v.epss === 'number' ? v.epss : (typeof v.epssScore === 'number' ? v.epssScore : 0);
      let score = 0;
      if (kev || euvdExploited) score += 50;
      score += (epss || 0) * 30;
      // Recency: published within 12m +10, 24m +5
      const pub = v.publishedDate ? new Date(v.publishedDate).getTime() : 0;
      if (pub) {
        const ageDays = (now - pub) / (1000 * 60 * 60 * 24);
        if (ageDays <= 365) score += 10;
        else if (ageDays <= 730) score += 5;
      }
      // Path-fit skipped here (needs phase[] in dossier context) - see plan
      return { ...v, _rankScore: Math.round(score * 10) / 10 };
    });
    scored.sort((a, b) => b._rankScore - a._rankScore);
    // Mark top 5
    return scored.map((v, i) => ({ ...v, _isTop: i < 5, _rank: i + 1 }));
  }

  // Enhanced MITRE ATT&CK techniques database
  getEnhancedTechniqueDatabase() {
    const database = {
      // Reconnaissance Phase Techniques
      reconnaissance: [
        { id: 'T1595.002', name: 'Vulnerability Scanning', tactic: 'Reconnaissance', description: 'Adversaries may scan victims for vulnerabilities that can be used during targeting.' },
        { id: 'T1592.002', name: 'Software', tactic: 'Reconnaissance', description: 'Adversaries may gather information about the victim\'s host software that can be used during targeting.' },
        { id: 'T1590.005', name: 'IP Addresses', tactic: 'Reconnaissance', description: 'Adversaries may gather the victim\'s IP addresses that can be used during targeting.' },
        { id: 'T1590.002', name: 'DNS', tactic: 'Reconnaissance', description: 'Adversaries may gather information about the victim\'s DNS that can be used during targeting.' },
        { id: 'T1589.002', name: 'Email Addresses', tactic: 'Reconnaissance', description: 'Adversaries may gather email addresses that can be used during targeting.' }
      ],

      // Initial Access Techniques
      initialAccess: [
        { id: 'T1190', name: 'Exploit Public-Facing Application', tactic: 'Initial Access', description: 'Adversaries may attempt to take advantage of a weakness in an Internet-facing computer or program.' },
        { id: 'T1566.001', name: 'Spearphishing Attachment', tactic: 'Initial Access', description: 'Adversaries may send spearphishing emails with a malicious attachment.' },
        { id: 'T1566.002', name: 'Spearphishing Link', tactic: 'Initial Access', description: 'Adversaries may send spearphishing emails with a malicious link.' },
        { id: 'T1078.004', name: 'Cloud Accounts', tactic: 'Initial Access', description: 'Adversaries may obtain and abuse credentials of a cloud account.' },
        { id: 'T1133', name: 'External Remote Services', tactic: 'Initial Access', description: 'Adversaries may leverage external-facing remote services to initially access.' }
      ],

      // Execution Techniques
      execution: [
        { id: 'T1059.001', name: 'PowerShell', tactic: 'Execution', description: 'Adversaries may abuse PowerShell commands and scripts for execution.' },
        { id: 'T1059.003', name: 'Windows Command Shell', tactic: 'Execution', description: 'Adversaries may abuse the Windows command shell for execution.' },
        { id: 'T1059.004', name: 'Unix Shell', tactic: 'Execution', description: 'Adversaries may abuse Unix shell commands and scripts for execution.' },
        { id: 'T1203', name: 'Exploitation for Client Execution', tactic: 'Execution', description: 'Adversaries may exploit software vulnerabilities in client applications to execute code.' },
        { id: 'T1204.002', name: 'Malicious File', tactic: 'Execution', description: 'An adversary may rely upon a user opening a malicious file in order to gain execution.' }
      ],

      // Persistence Techniques
      persistence: [
        { id: 'T1053.005', name: 'Scheduled Task', tactic: 'Persistence', description: 'Adversaries may abuse the Windows Task Scheduler to perform task scheduling for initial or recurring execution.' },
        { id: 'T1547.001', name: 'Registry Run Keys / Startup Folder', tactic: 'Persistence', description: 'Adversaries may achieve persistence by adding a program to a startup folder.' },
        { id: 'T1078', name: 'Valid Accounts', tactic: 'Persistence', description: 'Adversaries may obtain and abuse credentials of existing accounts.' },
        { id: 'T1543.003', name: 'Windows Service', tactic: 'Persistence', description: 'Adversaries may create or modify Windows services to repeatedly execute malicious payloads.' }
      ],

      // Credential Access Techniques
      credentialAccess: [
        { id: 'T1552.001', name: 'Credentials In Files', tactic: 'Credential Access', description: 'Adversaries may search local file systems and remote file shares for files containing insecurely stored credentials.' },
        { id: 'T1003.001', name: 'LSASS Memory', tactic: 'Credential Access', description: 'Adversaries may attempt to access credential material stored in the process memory of the Local Security Authority Subsystem Service.' },
        { id: 'T1110.001', name: 'Password Guessing', tactic: 'Credential Access', description: 'Adversaries may use a single or small list of commonly used passwords against many different accounts.' },
        { id: 'T1558.003', name: 'Kerberoasting', tactic: 'Credential Access', description: 'Adversaries may abuse a valid Kerberos ticket-granting ticket or sniff network traffic to obtain a ticket-granting service ticket.' }
      ],

      // Discovery Techniques
      discovery: [
        { id: 'T1018', name: 'Remote System Discovery', tactic: 'Discovery', description: 'Adversaries may attempt to get a listing of other systems by IP address, hostname, or other logical identifier.' },
        { id: 'T1083', name: 'File and Directory Discovery', tactic: 'Discovery', description: 'Adversaries may enumerate files and directories or may search in specific locations of a host or network share.' },
        { id: 'T1087.001', name: 'Local Account', tactic: 'Discovery', description: 'Adversaries may attempt to get a listing of local system accounts.' },
        { id: 'T1016', name: 'System Network Configuration Discovery', tactic: 'Discovery', description: 'Adversaries may look for details about the network configuration and settings.' }
      ],

      // Lateral Movement Techniques
      lateralMovement: [
        { id: 'T1021.001', name: 'Remote Desktop Protocol', tactic: 'Lateral Movement', description: 'Adversaries may use Valid Accounts to log into a computer using the Remote Desktop Protocol.' },
        { id: 'T1021.002', name: 'SMB/Windows Admin Shares', tactic: 'Lateral Movement', description: 'Adversaries may use Valid Accounts to interact with a remote network share using Server Message Block.' },
        { id: 'T1550.002', name: 'Pass the Hash', tactic: 'Lateral Movement', description: 'Adversaries may "pass the hash" using stolen password hashes to move laterally within an environment.' },
        { id: 'T1563.002', name: 'RDP Hijacking', tactic: 'Lateral Movement', description: 'Adversaries may hijack a legitimate user\'s remote desktop session to move laterally within an environment.' }
      ],

      // Collection Techniques
      collection: [
        { id: 'T1005', name: 'Data from Local System', tactic: 'Collection', description: 'Adversaries may search local system sources, such as file systems and configuration files or local databases.' },
        { id: 'T1039', name: 'Data from Network Shared Drive', tactic: 'Collection', description: 'Adversaries may search network shares on computers they have compromised to find files of interest.' },
        { id: 'T1560.001', name: 'Archive via Utility', tactic: 'Collection', description: 'Adversaries may use utilities to compress and/or encrypt collected data prior to exfiltration.' },
        { id: 'T1113', name: 'Screen Capture', tactic: 'Collection', description: 'Adversaries may attempt to take screen captures of the desktop to gather information over the course of an operation.' }
      ],

      // Exfiltration Techniques
      exfiltration: [
        { id: 'T1041', name: 'Exfiltration Over C2 Channel', tactic: 'Exfiltration', description: 'Adversaries may steal data by exfiltrating it over an existing command and control channel.' },
        { id: 'T1567.002', name: 'Exfiltration to Cloud Storage', tactic: 'Exfiltration', description: 'Adversaries may exfiltrate data to a cloud storage service rather than over their primary command and control channel.' },
        { id: 'T1048.003', name: 'Exfiltration Over Unencrypted Non-C2 Protocol', tactic: 'Exfiltration', description: 'Adversaries may steal data by exfiltrating it over an un-encrypted network protocol other than that of the existing command and control channel.' }
      ]
    };

    // Add URLs to all techniques
    Object.keys(database).forEach(category => {
      database[category] = database[category].map(technique => ({
        ...technique,
        url: this.generateMitreUrl(technique.id)
      }));
    });

    return database;
  }

  // Enhanced technique mapping based on asset types and vulnerabilities
  async getMitreAttackTechniques(assetType, vulnerabilities = []) {
    try {
      console.log(`[CTI] Getting enhanced MITRE ATT&CK techniques for asset type: ${assetType}`);

      const techniqueDb = this.getEnhancedTechniqueDatabase();
      let selectedTechniques = [];

      // Base techniques by asset type
      const assetTypeTechniques = {
        'Serveur': [
          ...techniqueDb.reconnaissance.slice(0, 2),
          ...techniqueDb.initialAccess.slice(0, 3),
          ...techniqueDb.execution.slice(0, 2),
          ...techniqueDb.persistence.slice(0, 2),
          ...techniqueDb.credentialAccess.slice(0, 2),
          ...techniqueDb.discovery.slice(0, 2),
          ...techniqueDb.lateralMovement.slice(0, 2),
          ...techniqueDb.collection.slice(0, 2),
          ...techniqueDb.exfiltration.slice(0, 2)
        ],
        'Base de données': [
          ...techniqueDb.reconnaissance.slice(0, 1),
          ...techniqueDb.initialAccess.slice(0, 2),
          ...techniqueDb.credentialAccess,
          ...techniqueDb.discovery.slice(1, 3),
          ...techniqueDb.collection,
          ...techniqueDb.exfiltration
        ],
        'Application': [
          ...techniqueDb.reconnaissance.slice(0, 2),
          ...techniqueDb.initialAccess,
          ...techniqueDb.execution,
          ...techniqueDb.persistence.slice(0, 2),
          ...techniqueDb.collection.slice(0, 2)
        ],
        'Réseau': [
          ...techniqueDb.reconnaissance,
          ...techniqueDb.credentialAccess.slice(0, 2),
          ...techniqueDb.discovery,
          ...techniqueDb.lateralMovement,
          ...techniqueDb.collection.slice(1, 3)
        ],
        'Équipement': [
          ...techniqueDb.reconnaissance.slice(0, 1),
          ...techniqueDb.initialAccess.slice(0, 2),
          ...techniqueDb.persistence.slice(0, 1),
          ...techniqueDb.discovery.slice(0, 2)
        ]
      };

      selectedTechniques = assetTypeTechniques[assetType] || [
        ...techniqueDb.reconnaissance.slice(0, 1),
        ...techniqueDb.initialAccess.slice(0, 2),
        ...techniqueDb.execution.slice(0, 1),
        ...techniqueDb.collection.slice(0, 1)
      ];

      // Enhance techniques based on vulnerabilities
      if (vulnerabilities && vulnerabilities.length > 0) {
        const vulnBasedTechniques = this.mapVulnerabilitiesToTechniques(vulnerabilities, techniqueDb);
        selectedTechniques = [...selectedTechniques, ...vulnBasedTechniques];
      }

      // Remove duplicates and limit to reasonable number
      const uniqueTechniques = selectedTechniques.filter((tech, index, self) =>
        index === self.findIndex(t => t.id === tech.id)
      ).slice(0, 15);

      console.log(`[CTI] Selected ${uniqueTechniques.length} techniques for ${assetType}`);
      return uniqueTechniques;

    } catch (error) {
      console.error(`[CTI] Error getting MITRE techniques:`, error);
      return [];
    }
  }

  // Map vulnerabilities to specific techniques
  mapVulnerabilitiesToTechniques(vulnerabilities, techniqueDb) {
    const additionalTechniques = [];

    vulnerabilities.forEach(vuln => {
      const description = vuln.description?.toLowerCase() || '';
      const severity = vuln.severity?.severity || '';

      // SQL Injection vulnerabilities
      if (description.includes('sql injection') || description.includes('sqli')) {
        additionalTechniques.push(
          { id: 'T1190', name: 'Exploit Public-Facing Application', tactic: 'Initial Access', description: 'SQL injection exploitation' },
          { id: 'T1005', name: 'Data from Local System', tactic: 'Collection', description: 'Database data extraction via SQL injection' }
        );
      }

      // Remote Code Execution vulnerabilities
      if (description.includes('remote code execution') || description.includes('rce')) {
        additionalTechniques.push(
          { id: 'T1190', name: 'Exploit Public-Facing Application', tactic: 'Initial Access', description: 'Remote code execution exploitation' },
          { id: 'T1059.003', name: 'Windows Command Shell', tactic: 'Execution', description: 'Command execution via RCE' }
        );
      }

      // Authentication bypass vulnerabilities
      if (description.includes('authentication bypass') || description.includes('auth bypass')) {
        additionalTechniques.push(
          { id: 'T1078', name: 'Valid Accounts', tactic: 'Initial Access', description: 'Authentication bypass exploitation' }
        );
      }

      // Directory traversal vulnerabilities
      if (description.includes('directory traversal') || description.includes('path traversal')) {
        additionalTechniques.push(
          { id: 'T1083', name: 'File and Directory Discovery', tactic: 'Discovery', description: 'Directory traversal exploitation' },
          { id: 'T1552.001', name: 'Credentials In Files', tactic: 'Credential Access', description: 'Credential file access via path traversal' }
        );
      }

      // High/Critical severity vulnerabilities get additional techniques
      if (severity === 'HIGH' || severity === 'CRITICAL') {
        additionalTechniques.push(
          ...techniqueDb.persistence.slice(0, 1),
          ...techniqueDb.lateralMovement.slice(0, 1)
        );
      }
    });

    return additionalTechniques;
  }

  // Main CTI analysis function
  // onProgress: optional callback ({ current, total, assetName, source, status })
  async performCTIAnalysis(selectedAssets, analysisType = 'combined', onProgress = null) {
    console.log(`[CTI] Starting ${analysisType} CTI analysis for assets:`, selectedAssets);

    const notify = (p) => {
      if (typeof onProgress === 'function') try { onProgress(p); } catch (_) {}
    };

    const dataSourceMap = {
      'nist': 'NIST NVD',
      'euvd': 'EUVD (EU Vulnerability Database)',
      'mitre': 'MITRE ATT&CK',
      'atlas': 'MITRE ATLAS',
      'combined': 'NIST + MITRE ATT&CK'
    };

    const results = {
      totalVulnerabilities: 0,
      totalAttackTechniques: 0,
      overallRiskScore: 0,
      assets: [],
      analysisDate: new Date().toISOString(),
      dataSource: dataSourceMap[analysisType] || 'Combined Sources',
      analysisType: analysisType
    };

    try {
      const total = selectedAssets.length;
      // Process each asset sequentially but with progress + asset-keyed cache
      for (let idx = 0; idx < selectedAssets.length; idx++) {
        const asset = selectedAssets[idx];
        const current = idx + 1;
        console.log(`[CTI] Analyzing asset ${current}/${total}: ${asset.name} with ${analysisType} analysis`);
        notify({ current, total, assetName: asset.name, source: analysisType, status: 'fetching', cacheHit: false });

        let vulnerabilities = [];
        let attackTechniques = [];
        let searchMethod = 'none';
        let cacheHit = false;

        // Vulnerabilities: asset-keyed cache (vendor:product:version:source)
        if (analysisType === 'nist' || analysisType === 'euvd' || analysisType === 'combined') {
          const cacheSource = analysisType === 'combined' ? 'nist' : analysisType;
          const cached = this.getAssetCached(asset, cacheSource);
          if (cached && cached.vulnerabilities) {
            vulnerabilities = cached.vulnerabilities;
            searchMethod = cached.searchMethod || 'cache';
            cacheHit = true;
            console.log(`[CTI] Cache hit for ${asset.name} (${cacheSource})`);
            notify({ current, total, assetName: asset.name, source: analysisType, status: 'cache-hit', cacheHit: true });
            if (vulnerabilities && vulnerabilities.length > 0 && vulnerabilities[0]._rankScore === undefined) {
              vulnerabilities = this.rankVulnerabilities(vulnerabilities);
            }
          } else {
            const searchResult = await this.searchVulnerabilities(asset, analysisType);
            if (searchResult && typeof searchResult === 'object' && searchResult.vulnerabilities) {
              vulnerabilities = searchResult.vulnerabilities;
              searchMethod = searchResult.searchMethod;
            } else {
              vulnerabilities = Array.isArray(searchResult) ? searchResult : [];
            }
            // Enrich + rank (KEV/EPSS) before caching so cache stores enriched list
            if (vulnerabilities && vulnerabilities.length > 0) {
              try {
                notify({ current, total, assetName: asset.name, source: analysisType, status: 'enriching', cacheHit });
                vulnerabilities = await this.enrichWithKevEpss(vulnerabilities);
                vulnerabilities = this.rankVulnerabilities(vulnerabilities);
              } catch (_) {}
            }
            // Store in cache for next path/retry
            if (vulnerabilities) {
              this.setAssetCached(asset, cacheSource, { vulnerabilities, searchMethod });
            }
          }
        }

        if (analysisType === 'mitre' || analysisType === 'combined') {
          notify({ current, total, assetName: asset.name, source: analysisType, status: 'mitre', cacheHit });
          attackTechniques = await this.getLiveMitreAttackTechniques(asset, vulnerabilities);
        }

        if (analysisType === 'atlas') {
          notify({ current, total, assetName: asset.name, source: analysisType, status: 'atlas', cacheHit });
          attackTechniques = await this.getAtlasThreats(asset);
        }

        // Calculate risk score for this asset
        const assetRiskScore = this.calculateAssetRiskScore(vulnerabilities, attackTechniques, asset.criticality);

        const assetResult = {
          ...asset,
          vulnerabilities: vulnerabilities,
          vulnerabilityCount: vulnerabilities.length,
          attackTechniques: attackTechniques,
          techniqueCount: attackTechniques.length,
          riskScore: assetRiskScore,
          searchMethod: searchMethod,
          _cacheHit: cacheHit,
          highSeverityVulns: vulnerabilities.filter(v =>
            v.severity.severity === 'HIGH' || v.severity.severity === 'CRITICAL'
          ).length
        };

        results.assets.push(assetResult);
        results.totalVulnerabilities += vulnerabilities.length;
        results.totalAttackTechniques += attackTechniques.length;
        notify({ current, total, assetName: asset.name, source: analysisType, status: 'done', cacheHit });

        // Throttle: backend now queues NVD per-IP, so frontend only needs tiny gap
        // Keep 300ms between assets for EUVD/MITRE/ATLAS to avoid burst, 0 for cache hits
        if (!cacheHit && idx < selectedAssets.length - 1) {
          const gap = (analysisType === 'euvd' || analysisType === 'mitre' || analysisType === 'atlas') ? 300 : 0;
          if (gap) await this.delay(gap);
        }
      }

      // Calculate overall risk score
      results.overallRiskScore = this.calculateOverallRiskScore(results.assets);

      console.log('[CTI] CTI analysis completed:', results);
      return results;

    } catch (error) {
      console.error('[CTI] Error during CTI analysis:', error);
      throw error;
    }
  }

  // Calculate risk score for individual asset
  calculateAssetRiskScore(vulnerabilities, attackTechniques, criticality) {
    let score = 0;

    // Base score from vulnerabilities
    vulnerabilities.forEach(vuln => {
      score += vuln.severity.score || 0;
    });

    // Add points for attack techniques
    score += attackTechniques.length * 0.5;

    // Multiply by criticality factor
    const criticalityMultiplier = {
      'critique': 1.5,
      'élevé': 1.3,
      'moyen': 1.0,
      'faible': 0.7
    };

    score *= criticalityMultiplier[criticality] || 1.0;

    // Normalize to 0-10 scale
    return Math.min(Math.round(score * 10) / 10, 10);
  }

  // Calculate overall risk score
  calculateOverallRiskScore(assets) {
    if (assets.length === 0) return 0;

    const totalScore = assets.reduce((sum, asset) => sum + asset.riskScore, 0);
    return Math.round((totalScore / assets.length) * 10) / 10;
  }

  // Fetch live MITRE ATT&CK data from GitHub repository
  async fetchLiveMitreData() {
    try {
      console.log('[CTI] Fetching live MITRE ATT&CK data...');

      // Check cache first
      const now = Date.now();
      if (this.mitreCache.data && this.mitreCache.lastUpdated &&
        (now - this.mitreCache.lastUpdated) < this.mitreCache.cacheExpiry) {
        console.log('[CTI] Using cached MITRE data');
        return this.mitreCache.data;
      }

      const response = await fetch(this.mitreStixUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'EBIOS-RM-CTI-Service/1.0'
        }
      });

      if (!response.ok) {
        console.error(`[CTI] MITRE API error: ${response.status} ${response.statusText}`);
        return this.getFallbackMitreData();
      }

      const stixData = await response.json();
      console.log('[CTI] Successfully fetched MITRE STIX data');
      console.log('[CTI] MITRE STIX response structure:', {
        objectsCount: stixData.objects?.length || 0,
        firstObject: stixData.objects?.[0] || null,
        objectTypes: [...new Set(stixData.objects?.map(obj => obj.type) || [])]
      });

      // Parse STIX data to extract techniques
      const techniques = this.parseMitreStixData(stixData);

      // Update cache
      this.mitreCache.data = techniques;
      this.mitreCache.lastUpdated = now;

      console.log(`[CTI] Parsed ${techniques.length} techniques from live MITRE data`);
      return techniques;

    } catch (error) {
      console.error('[CTI] Error fetching live MITRE data:', error);
      return this.getFallbackMitreData();
    }
  }

  // Parse MITRE STIX data to extract techniques
  parseMitreStixData(stixData) {
    const techniques = [];

    if (!stixData.objects) {
      console.warn('[CTI] No objects found in MITRE STIX data');
      return this.getFallbackMitreData();
    }

    stixData.objects.forEach((obj, index) => {
      if (obj.type === 'attack-pattern' && !obj.revoked && !obj.x_mitre_deprecated) {
        // Log first few technique objects for structure analysis
        if (index < 3) {
          console.log(`[CTI] Raw MITRE technique object ${index + 1}:`, obj);
        }

        // Extract technique information
        const technique = {
          id: obj.external_references?.find(ref => ref.source_name === 'mitre-attack')?.external_id || 'Unknown',
          name: obj.name || 'Unknown Technique',
          description: obj.description || 'No description available',
          tactic: obj.kill_chain_phases?.map(phase => phase.phase_name).join(', ') || 'Unknown',
          platforms: obj.x_mitre_platforms || [],
          dataSource: obj.x_mitre_data_sources || [],
          version: obj.x_mitre_version || '1.0',
          lastModified: obj.modified || obj.created,
          url: this.generateMitreUrl(obj.external_references?.find(ref => ref.source_name === 'mitre-attack')?.external_id),
          // Enhanced fields from STIX data
          created: obj.created,
          modified: obj.modified,
          type: obj.type,
          spec_version: obj.spec_version,
          external_references: obj.external_references || [],
          kill_chain_phases: obj.kill_chain_phases || [],
          x_mitre_detection: obj.x_mitre_detection,
          x_mitre_is_subtechnique: obj.x_mitre_is_subtechnique,
          x_mitre_permissions_required: obj.x_mitre_permissions_required || [],
          x_mitre_effective_permissions: obj.x_mitre_effective_permissions || [],
          x_mitre_system_requirements: obj.x_mitre_system_requirements || [],
          x_mitre_impact_type: obj.x_mitre_impact_type || [],
          x_mitre_defense_bypassed: obj.x_mitre_defense_bypassed || [],
          x_mitre_remote_support: obj.x_mitre_remote_support,
          x_mitre_network_requirements: obj.x_mitre_network_requirements
        };

        // Log first processed technique for structure analysis
        if (index < 3) {
          console.log(`[CTI] Processed MITRE technique ${index + 1}:`, technique);
        }

        // Only include techniques with valid IDs
        if (technique.id.startsWith('T')) {
          techniques.push(technique);
        }
      }
    });

    return techniques;
  }

  // Fallback to enhanced static data if live fetch fails
  getFallbackMitreData() {
    console.log('[CTI] Using fallback enhanced MITRE data');
    const techniqueDb = this.getEnhancedTechniqueDatabase();

    return [
      ...techniqueDb.reconnaissance,
      ...techniqueDb.initialAccess,
      ...techniqueDb.execution,
      ...techniqueDb.persistence,
      ...techniqueDb.credentialAccess,
      ...techniqueDb.discovery,
      ...techniqueDb.lateralMovement,
      ...techniqueDb.collection,
      ...techniqueDb.exfiltration
    ];
  }

  // Enhanced technique selection: prefer CWE-derived, fallback to keyword
  async getLiveMitreAttackTechniques(asset, vulnerabilities = null) {
    try {
      // Slice 3: if we have vulns with CWE, derive technique IDs directly
      if (vulnerabilities && vulnerabilities.length > 0) {
        const derivedIds = this.deriveTechniqueIdsFromVulns(vulnerabilities);
        if (derivedIds.length > 0) {
          console.log(`[CTI] Derived ${derivedIds.length} technique IDs from CWE:`, derivedIds);
          const derived = await this.fetchTechniquesByIds(derivedIds);
          if (derived.length > 0) {
            console.log(`[CTI] CWE-derived MITRE success: ${derived.length} techniques`);
            // Fill up to 10 with keyword fallback if needed
            if (derived.length < 5) {
              const fallback = await this.fetchFromLocalMitreApi(asset);
              const extra = fallback.filter((t) => !derived.some((d) => d.id === t.id)).slice(0, 5 - derived.length);
              return [...derived, ...extra];
            }
            return derived.slice(0, 10);
          }
        }
      }
      console.log(`[CTI] Getting MITRE ATT&CK techniques from local API for asset: ${asset.name}`);
      const localTechniques = await this.fetchFromLocalMitreApi(asset);
      if (localTechniques && localTechniques.length > 0) {
        console.log(`[CTI] Successfully retrieved ${localTechniques.length} techniques from local MITRE API`);
        return localTechniques;
      }
      console.log(`[CTI] No techniques found from local MITRE API for asset: ${asset.name}`);
      return [];
    } catch (error) {
      console.error(`[CTI] Error getting MITRE techniques from local API:`, error);
      return [];
    }
  }

  // Fetch techniques from local MITRE API server
  async fetchFromLocalMitreApi(asset) {
    try {
      console.log(`[CTI] Fetching from local MITRE API for asset: ${asset.name}`);

      // Generate search keywords based on asset product names
      const searchKeywords = this.generateMitreSearchKeywords(asset);

      let allTechniques = [];

      // Search for each keyword (search both main techniques and sub-techniques)
      for (const keyword of searchKeywords) {
        console.log(`[CTI] Searching local MITRE API with keyword: "${keyword}"`);

        // Search both main techniques and sub-techniques
        const searchTypes = ['false', 'true']; // isSubtechnique values

        for (const isSubTechnique of searchTypes) {
          console.log(`[CTI] Searching for ${isSubTechnique === 'true' ? 'sub-' : 'main '}techniques with keyword: "${keyword}"`);

          const params = new URLSearchParams({
            q: keyword,
            type: 'attack-pattern',
            isSubtechnique: isSubTechnique,
            // Pass asset type for backend fallback if initial search returns 0 results
            assetType: asset.nature || asset.type || ''
          });

          const response = await fetch(`${this.localMitreAttackUrl}?${params}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json'
            },
            timeout: 10000 // 10 second timeout
          });

          if (!response.ok) {
            console.warn(`[CTI] Local MITRE ATT&CK API error for keyword "${keyword}" (subtechnique: ${isSubTechnique}): ${response.status}`);
            continue;
          }

          const response_data = await response.json();

          // Handle backend proxy response format
          const data = response_data.success ? response_data.data : response_data;

          console.log(`[CTI] Local MITRE ATT&CK API response for "${keyword}" (subtechnique: ${isSubTechnique}):`, {
            success: response_data.success,
            source: response_data.source,
            resultsCount: Array.isArray(data) ? data.length : 0
          });

          // Process ATT&CK techniques (data is now a direct array)
          if (Array.isArray(data) && data.length > 0) {
            const techniques = data.map(tech => this.parseLocalMitreTechnique(tech));
            allTechniques = [...allTechniques, ...techniques];
          }
        }
      }

      // Remove duplicates and limit results
      const uniqueTechniques = allTechniques.filter((tech, index, self) =>
        index === self.findIndex(t => t.id === tech.id)
      ).slice(0, 15);

      // Log technique diversity for debugging
      const techniqueIds = uniqueTechniques.map(t => t.id);
      const tacticCounts = {};
      uniqueTechniques.forEach(tech => {
        const tactic = tech.tactic || 'Unknown';
        tacticCounts[tactic] = (tacticCounts[tactic] || 0) + 1;
      });

      console.log(`[CTI] Local MITRE API returned ${uniqueTechniques.length} unique techniques`);
      console.log(`[CTI] Technique IDs:`, techniqueIds);
      console.log(`[CTI] Tactic distribution:`, tacticCounts);
      console.log(`[CTI] Sources:`, [...new Set(uniqueTechniques.map(t => t.source))]);

      return uniqueTechniques;

    } catch (error) {
      console.error(`[CTI] Error fetching from local MITRE API:`, error);
      return [];
    }
  }

  // NEW: Fetch ALL MITRE object types (techniques, threat groups, malware, tools, campaigns)
  async fetchAllMitreTypes(asset) {
    try {
      console.log(`[CTI] Fetching ALL MITRE types for asset: ${asset.name}`);

      // Generate search keywords based on asset product names
      const searchKeywords = this.generateMitreSearchKeywords(asset);

      let allResults = {
        techniques: [],
        threatGroups: [],
        malware: [],
        tools: [],
        campaigns: [],
        totalCount: 0
      };

      // Search for each keyword
      for (const keyword of searchKeywords) {
        console.log(`[CTI] Searching all MITRE types with keyword: "${keyword}"`);

        const response = await fetch(`${this.localMitreAttackAllUrl}?q=${encodeURIComponent(keyword)}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          console.warn(`[CTI] MITRE all-types API error for keyword "${keyword}": ${response.status}`);
          continue;
        }

        const responseData = await response.json();
        const data = responseData.success ? responseData.data : responseData;

        console.log(`[CTI] MITRE all-types response for "${keyword}":`, {
          success: responseData.success,
          techniques: data.techniques?.length || 0,
          threatGroups: data.threatGroups?.length || 0,
          malware: data.malware?.length || 0,
          tools: data.tools?.length || 0,
          campaigns: data.campaigns?.length || 0,
          totalCount: data.totalCount || 0
        });

        // Merge results (avoiding duplicates)
        if (data.techniques) {
          data.techniques.forEach(t => {
            if (!allResults.techniques.find(existing => existing.id === t.id)) {
              allResults.techniques.push(t);
            }
          });
        }
        if (data.threatGroups) {
          data.threatGroups.forEach(g => {
            if (!allResults.threatGroups.find(existing => existing.id === g.id)) {
              allResults.threatGroups.push(g);
            }
          });
        }
        if (data.malware) {
          data.malware.forEach(m => {
            if (!allResults.malware.find(existing => existing.id === m.id)) {
              allResults.malware.push(m);
            }
          });
        }
        if (data.tools) {
          data.tools.forEach(t => {
            if (!allResults.tools.find(existing => existing.id === t.id)) {
              allResults.tools.push(t);
            }
          });
        }
        if (data.campaigns) {
          data.campaigns.forEach(c => {
            if (!allResults.campaigns.find(existing => existing.id === c.id)) {
              allResults.campaigns.push(c);
            }
          });
        }
      }

      // Update total count
      allResults.totalCount =
        allResults.techniques.length +
        allResults.threatGroups.length +
        allResults.malware.length +
        allResults.tools.length +
        allResults.campaigns.length;

      console.log(`[CTI] Total MITRE results for ${asset.name}:`, {
        techniques: allResults.techniques.length,
        threatGroups: allResults.threatGroups.length,
        malware: allResults.malware.length,
        tools: allResults.tools.length,
        campaigns: allResults.campaigns.length,
        total: allResults.totalCount
      });

      return allResults;

    } catch (error) {
      console.error(`[CTI] Error fetching all MITRE types:`, error);
      return {
        techniques: [],
        threatGroups: [],
        malware: [],
        tools: [],
        campaigns: [],
        totalCount: 0
      };
    }
  }

  // Generate search keywords based on business asset vendor, product, and version for more meaningful results
  generateMitreSearchKeywords(asset) {
    const keywords = new Set(); // Use Set to avoid duplicates

    if (!asset) {
      console.log(`[CTI] No asset provided for MITRE search`);
      return [];
    }

    console.log(`[CTI] Generating MITRE search keywords for asset:`, {
      name: asset.name,
      vendor: asset.vendor,
      product: asset.product,
      version: asset.version,
      type: asset.type
    });

    // Priority 1: Use vendor if available (e.g., "Microsoft", "Apache", "Oracle")
    if (asset.vendor && asset.vendor.trim()) {
      keywords.add(asset.vendor.trim());
      console.log(`[CTI] Added vendor as MITRE search keyword: '${asset.vendor}'`);
    }

    // Priority 2: Use product if available (e.g., "Windows Server", "MySQL", "HTTP Server")
    if (asset.product && asset.product.trim()) {
      keywords.add(asset.product.trim());
      console.log(`[CTI] Added product as MITRE search keyword: '${asset.product}'`);
    }

    // Priority 3: Combine vendor + product for more specific search (e.g., "Apache HTTP Server")
    if (asset.vendor && asset.product && asset.vendor.trim() && asset.product.trim()) {
      const combinedKeyword = `${asset.vendor.trim()} ${asset.product.trim()}`;
      keywords.add(combinedKeyword);
      console.log(`[CTI] Added combined vendor+product as MITRE search keyword: '${combinedKeyword}'`);
    }

    // Priority 4: Add version-specific search if version is available (for specific vulnerability matching)
    if (asset.product && asset.version && asset.product.trim() && asset.version.trim()) {
      const productVersion = `${asset.product.trim()} ${asset.version.trim()}`;
      keywords.add(productVersion);
      console.log(`[CTI] Added product+version as MITRE search keyword: '${productVersion}'`);
    }

    // Priority 5: FALLBACK - Add attack-relevant keywords based on asset TYPE/NATURE
    // This is crucial because MITRE ATT&CK catalogs attack techniques, not products
    const assetTypeKeywords = this.getAssetTypeAttackKeywords(asset);
    assetTypeKeywords.forEach(kw => {
      keywords.add(kw);
      console.log(`[CTI] Added asset-type-based MITRE keyword: '${kw}'`);
    });

    // Fallback: Use asset name if no vendor/product specified
    if (keywords.size === 0 && asset.name && asset.name.trim()) {
      keywords.add(asset.name.trim());
      console.log(`[CTI] No vendor/product specified, using asset name as MITRE search keyword: '${asset.name}'`);
    }

    const finalKeywords = Array.from(keywords);
    console.log(`[CTI] Generated ${finalKeywords.length} MITRE search keywords for ${asset.name}:`, finalKeywords);

    // Limit to reasonable number of keywords to avoid too many API calls
    return finalKeywords.slice(0, 8);
  }

  // NEW: Get attack-relevant keywords based on asset type/nature
  // Maps asset types to MITRE ATT&CK relevant search terms
  // Comprehensive product detection covering 100+ technologies
  getAssetTypeAttackKeywords(asset) {
    const keywords = [];

    // Determine asset type from type field or name
    const assetType = asset.type?.toLowerCase() || '';
    const assetName = asset.name?.toLowerCase() || '';
    const product = asset.product?.toLowerCase() || '';
    const vendor = asset.vendor?.toLowerCase() || '';
    const combined = `${product} ${assetName} ${vendor}`;

    // ============================================
    // WEB APPLICATIONS & CMS
    // ============================================
    const webCmsProducts = [
      'wordpress', 'drupal', 'joomla', 'magento', 'prestashop', 'shopify',
      'woocommerce', 'opencart', 'typo3', 'contentful', 'strapi', 'ghost',
      'squarespace', 'wix', 'webflow', 'hubspot', 'sitecore', 'kentico',
      'umbraco', 'episerver', 'aem', 'adobe experience', 'liferay', 'sharepoint',
      'confluence', 'mediawiki', 'dokuwiki', 'xwiki', 'plone', 'moodle',
      'canvas', 'blackboard', 'sakai', 'chamilo'
    ];
    if (webCmsProducts.some(p => combined.includes(p)) ||
      assetName.includes('web') || assetName.includes('site') ||
      assetName.includes('portal') || assetName.includes('intranet') ||
      assetType.includes('application') || assetType.includes('cms')) {
      keywords.push('web application');
      keywords.push('exploit public');
      keywords.push('web shell');
      keywords.push('injection');
      console.log(`[CTI] Detected Web Application/CMS - adding web-related attack keywords`);
    }

    // ============================================
    // WEB FRAMEWORKS & LANGUAGES
    // ============================================
    const frameworkProducts = [
      'react', 'angular', 'vue', 'next.js', 'nextjs', 'nuxt', 'svelte',
      'django', 'flask', 'fastapi', 'pyramid', 'tornado',
      'spring', 'spring boot', 'springboot', 'struts', 'hibernate',
      'laravel', 'symfony', 'codeigniter', 'yii', 'cakephp', 'zend',
      'ruby on rails', 'rails', 'sinatra', 'hanami',
      'express', 'express.js', 'node.js', 'nodejs', 'deno', 'bun',
      'asp.net', 'aspnet', '.net', 'dotnet', 'blazor',
      'php', 'perl', 'python', 'ruby', 'java', 'golang', 'go lang', 'rust'
    ];
    if (frameworkProducts.some(p => combined.includes(p))) {
      keywords.push('code injection');
      keywords.push('server-side');
      keywords.push('deserialization');
      keywords.push('command execution');
      console.log(`[CTI] Detected Web Framework - adding code execution attack keywords`);
    }

    // ============================================
    // DATABASES (SQL & NoSQL)
    // ============================================
    const databaseProducts = [
      'mysql', 'mariadb', 'postgresql', 'postgres', 'oracle', 'sql server',
      'mssql', 'sqlite', 'db2', 'sybase', 'informix', 'teradata',
      'mongodb', 'couchdb', 'couchbase', 'cassandra', 'redis', 'memcached',
      'elasticsearch', 'opensearch', 'solr', 'neo4j', 'arangodb', 'orientdb',
      'dynamodb', 'cosmos', 'firestore', 'firebase', 'supabase',
      'cockroachdb', 'timescaledb', 'influxdb', 'clickhouse', 'duckdb',
      'snowflake', 'bigquery', 'redshift', 'databricks', 'hive', 'presto'
    ];
    if (databaseProducts.some(p => combined.includes(p)) ||
      combined.includes('database') || combined.includes('bdd') ||
      assetType.includes('base de données') || assetType.includes('database')) {
      keywords.push('sql');
      keywords.push('credential');
      keywords.push('data');
      keywords.push('exfiltration');
      console.log(`[CTI] Detected Database - adding data/credential attack keywords`);
    }

    // ============================================
    // OPERATING SYSTEMS
    // ============================================
    const osProducts = [
      'windows', 'windows server', 'windows 10', 'windows 11',
      'linux', 'ubuntu', 'debian', 'centos', 'rhel', 'red hat', 'fedora',
      'suse', 'opensuse', 'arch', 'gentoo', 'alpine', 'kali',
      'macos', 'mac os', 'darwin', 'freebsd', 'openbsd', 'netbsd',
      'solaris', 'aix', 'hp-ux', 'unix', 'vmware esxi', 'proxmox',
      'android', 'ios', 'iphone', 'ipad', 'chromeos'
    ];
    if (osProducts.some(p => combined.includes(p)) ||
      assetType.includes('serveur') || assetType.includes('server') ||
      assetType.includes('os') || assetType.includes('système')) {
      keywords.push('remote');
      keywords.push('privilege escalation');
      keywords.push('lateral movement');
      keywords.push('persistence');
      keywords.push('command shell');
      console.log(`[CTI] Detected Operating System - adding OS attack keywords`);
    }

    // ============================================
    // WEB SERVERS & REVERSE PROXIES
    // ============================================
    const webServerProducts = [
      'apache', 'httpd', 'nginx', 'iis', 'lighttpd', 'caddy',
      'tomcat', 'jetty', 'wildfly', 'jboss', 'weblogic', 'websphere',
      'glassfish', 'payara', 'undertow', 'netty',
      'haproxy', 'traefik', 'envoy', 'kong', 'varnish', 'squid'
    ];
    if (webServerProducts.some(p => combined.includes(p))) {
      keywords.push('exploit public');
      keywords.push('server');
      keywords.push('web service');
      keywords.push('denial of service');
      console.log(`[CTI] Detected Web Server - adding server attack keywords`);
    }

    // ============================================
    // NETWORK EQUIPMENT & SECURITY
    // ============================================
    const networkProducts = [
      'cisco', 'juniper', 'palo alto', 'fortinet', 'fortigate', 'checkpoint',
      'sonicwall', 'watchguard', 'sophos', 'barracuda', 'f5', 'big-ip',
      'netgear', 'linksys', 'tp-link', 'ubiquiti', 'unifi', 'mikrotik',
      'aruba', 'meraki', 'ruckus', 'extreme', 'brocade', 'dell emc',
      'router', 'switch', 'firewall', 'waf', 'ids', 'ips', 'proxy',
      'vpn', 'openvpn', 'wireguard', 'ipsec', 'ssl vpn', 'sslvpn'
    ];
    if (networkProducts.some(p => combined.includes(p)) ||
      assetType.includes('réseau') || assetType.includes('network')) {
      keywords.push('network');
      keywords.push('man-in-the-middle');
      keywords.push('sniffing');
      keywords.push('remote service');
      keywords.push('adversary-in-the-middle');
      console.log(`[CTI] Detected Network Equipment - adding network attack keywords`);
    }

    // ============================================
    // EMAIL & MESSAGING
    // ============================================
    const emailProducts = [
      'exchange', 'outlook', 'office 365', 'microsoft 365', 'm365',
      'gmail', 'google workspace', 'g suite',
      'postfix', 'sendmail', 'exim', 'qmail', 'dovecot', 'zimbra',
      'protonmail', 'tutanota', 'fastmail', 'zoho mail',
      'smtp', 'imap', 'pop3', 'mta', 'mda',
      'slack', 'teams', 'discord', 'mattermost', 'rocket.chat', 'zulip'
    ];
    if (emailProducts.some(p => combined.includes(p)) ||
      combined.includes('email') || combined.includes('mail') ||
      combined.includes('messagerie') || combined.includes('courriel')) {
      keywords.push('phishing');
      keywords.push('spearphishing');
      keywords.push('email');
      keywords.push('credential');
      keywords.push('business email compromise');
      console.log(`[CTI] Detected Email/Messaging - adding email attack keywords`);
    }

    // ============================================
    // CLOUD PLATFORMS & SERVICES
    // ============================================
    const cloudProducts = [
      'aws', 'amazon web services', 'ec2', 's3', 'lambda', 'rds',
      'azure', 'microsoft azure', 'azure ad', 'entra',
      'gcp', 'google cloud', 'compute engine', 'cloud run',
      'oracle cloud', 'oci', 'ibm cloud', 'alibaba cloud', 'aliyun',
      'digitalocean', 'linode', 'vultr', 'hetzner', 'ovh', 'scaleway',
      'heroku', 'vercel', 'netlify', 'render', 'railway', 'fly.io',
      'cloudflare', 'akamai', 'fastly', 'cdn'
    ];
    if (cloudProducts.some(p => combined.includes(p)) ||
      combined.includes('cloud') || combined.includes('saas') ||
      combined.includes('paas') || combined.includes('iaas')) {
      keywords.push('cloud');
      keywords.push('cloud account');
      keywords.push('cloud service');
      keywords.push('cloud infrastructure');
      console.log(`[CTI] Detected Cloud Platform - adding cloud attack keywords`);
    }

    // ============================================
    // CONTAINERS & ORCHESTRATION
    // ============================================
    const containerProducts = [
      'docker', 'containerd', 'podman', 'cri-o', 'lxc', 'lxd',
      'kubernetes', 'k8s', 'k3s', 'openshift', 'rancher', 'nomad',
      'helm', 'istio', 'linkerd', 'consul', 'vault',
      'docker compose', 'docker swarm', 'amazon ecs', 'fargate', 'aks', 'gke', 'eks'
    ];
    if (containerProducts.some(p => combined.includes(p))) {
      keywords.push('container');
      keywords.push('escape');
      keywords.push('cloud');
      keywords.push('privilege escalation');
      console.log(`[CTI] Detected Container/Orchestration - adding container attack keywords`);
    }

    // ============================================
    // CI/CD & DEVOPS
    // ============================================
    const devopsProducts = [
      'jenkins', 'gitlab', 'github', 'bitbucket', 'azure devops', 'teamcity',
      'circleci', 'travis', 'bamboo', 'drone', 'argo', 'flux', 'spinnaker',
      'ansible', 'puppet', 'chef', 'saltstack', 'terraform', 'pulumi',
      'vagrant', 'packer', 'waypoint', 'atlantis',
      'git', 'svn', 'mercurial', 'perforce'
    ];
    if (devopsProducts.some(p => combined.includes(p))) {
      keywords.push('supply chain');
      keywords.push('code repository');
      keywords.push('ci/cd');
      keywords.push('trusted developer');
      console.log(`[CTI] Detected CI/CD/DevOps - adding supply chain attack keywords`);
    }

    // ============================================
    // IDENTITY & ACCESS MANAGEMENT
    // ============================================
    const identityProducts = [
      'active directory', 'ad', 'ldap', 'azure ad', 'entra id',
      'okta', 'auth0', 'ping identity', 'onelogin', 'jumpcloud',
      'keycloak', 'forgerock', 'sailpoint', 'cyberark', 'beyond trust',
      'radius', 'tacacs', 'saml', 'oauth', 'oidc', 'openid',
      'mfa', '2fa', 'duo', 'rsa securid', 'yubikey', 'fido'
    ];
    if (identityProducts.some(p => combined.includes(p)) ||
      combined.includes('authentication') || combined.includes('identity') ||
      combined.includes('sso') || combined.includes('authentification')) {
      keywords.push('credential');
      keywords.push('authentication');
      keywords.push('brute force');
      keywords.push('kerberos');
      keywords.push('valid accounts');
      console.log(`[CTI] Detected Identity/Auth - adding authentication attack keywords`);
    }

    // ============================================
    // ERP & BUSINESS APPLICATIONS
    // ============================================
    const erpProducts = [
      'sap', 'sap hana', 'sap erp', 'sap s/4hana',
      'oracle erp', 'oracle ebs', 'peoplesoft', 'jd edwards',
      'microsoft dynamics', 'dynamics 365', 'navision', 'ax',
      'salesforce', 'servicenow', 'workday', 'netsuite',
      'odoo', 'erpnext', 'sage', 'infor', 'epicor', 'ifs'
    ];
    if (erpProducts.some(p => combined.includes(p)) ||
      combined.includes('erp') || combined.includes('crm') ||
      combined.includes('gestion') || combined.includes('rh')) {
      keywords.push('data');
      keywords.push('financial theft');
      keywords.push('credential');
      keywords.push('valid accounts');
      console.log(`[CTI] Detected ERP/Business App - adding business attack keywords`);
    }

    // ============================================
    // MONITORING & LOGGING
    // ============================================
    const monitoringProducts = [
      'splunk', 'elastic', 'elk', 'kibana', 'logstash', 'filebeat',
      'datadog', 'new relic', 'dynatrace', 'appdynamics', 'instana',
      'prometheus', 'grafana', 'zabbix', 'nagios', 'icinga', 'checkmk',
      'pagerduty', 'opsgenie', 'victorops', 'sentry', 'rollbar',
      'graylog', 'fluentd', 'loki', 'jaeger', 'zipkin'
    ];
    if (monitoringProducts.some(p => combined.includes(p)) ||
      combined.includes('siem') || combined.includes('log') ||
      combined.includes('monitoring') || combined.includes('supervision')) {
      keywords.push('indicator removal');
      keywords.push('impair defenses');
      keywords.push('log');
      keywords.push('defense evasion');
      console.log(`[CTI] Detected Monitoring/Logging - adding defense evasion keywords`);
    }

    // ============================================
    // VIRTUALIZATION
    // ============================================
    const virtProducts = [
      'vmware', 'vsphere', 'vcenter', 'esxi', 'nsx',
      'hyper-v', 'hyperv',
      'virtualbox', 'vbox',
      'kvm', 'qemu', 'libvirt', 'proxmox', 'ovirt',
      'xen', 'citrix', 'xenserver', 'xendesktop', 'xenapp',
      'virtual', 'vm', 'hypervisor'
    ];
    if (virtProducts.some(p => combined.includes(p))) {
      keywords.push('virtual');
      keywords.push('escape');
      keywords.push('hypervisor');
      keywords.push('lateral movement');
      console.log(`[CTI] Detected Virtualization - adding VM attack keywords`);
    }

    // ============================================
    // IoT & INDUSTRIAL (OT/ICS/SCADA)
    // ============================================
    const iotProducts = [
      'iot', 'raspberry', 'arduino', 'esp32', 'esp8266',
      'scada', 'plc', 'hmi', 'dcs', 'rtu', 'ics',
      'siemens', 'schneider', 'rockwell', 'allen bradley', 'honeywell', 'abb',
      'modbus', 'bacnet', 'dnp3', 'opc', 'profinet', 'ethercat',
      'smart', 'sensor', 'camera', 'cctv', 'hikvision', 'dahua'
    ];
    if (iotProducts.some(p => combined.includes(p)) ||
      combined.includes('industriel') || combined.includes('automate')) {
      keywords.push('firmware');
      keywords.push('embedded');
      keywords.push('ics');
      keywords.push('physical');
      console.log(`[CTI] Detected IoT/Industrial - adding OT/ICS attack keywords`);
    }

    // ============================================
    // BACKUP & STORAGE
    // ============================================
    const storageProducts = [
      'veeam', 'acronis', 'commvault', 'veritas', 'backup exec',
      'netbackup', 'avamar', 'data domain', 'cohesity', 'rubrik',
      'nas', 'san', 'netapp', 'emc', 'dell emc', 'pure storage',
      'qnap', 'synology', 'drobo', 'truenas', 'freenas',
      'ceph', 'glusterfs', 'minio', 'swift', 'hdfs'
    ];
    if (storageProducts.some(p => combined.includes(p)) ||
      combined.includes('backup') || combined.includes('sauvegarde') ||
      combined.includes('storage') || combined.includes('stockage')) {
      keywords.push('data');
      keywords.push('inhibit recovery');
      keywords.push('destruction');
      keywords.push('ransomware');
      console.log(`[CTI] Detected Backup/Storage - adding data destruction attack keywords`);
    }

    // ============================================
    // SECURITY TOOLS
    // ============================================
    const securityProducts = [
      'antivirus', 'edr', 'xdr', 'mdp', 'mdr',
      'crowdstrike', 'sentinelone', 'carbon black', 'defender', 'cortex',
      'symantec', 'mcafee', 'kaspersky', 'bitdefender', 'malwarebytes',
      'qualys', 'tenable', 'nessus', 'rapid7', 'nexpose', 'openvas',
      'burp', 'zap', 'nmap', 'metasploit', 'cobalt strike'
    ];
    if (securityProducts.some(p => combined.includes(p))) {
      keywords.push('impair defenses');
      keywords.push('disable security');
      keywords.push('indicator removal');
      keywords.push('defense evasion');
      console.log(`[CTI] Detected Security Tool - adding defense evasion keywords`);
    }

    // ============================================
    // MOBILE & ENDPOINT
    // ============================================
    const mobileProducts = [
      'mobile', 'smartphone', 'tablet', 'mdm', 'mam', 'emm', 'uem',
      'intune', 'jamf', 'workspace one', 'mobileiron', 'airwatch',
      'android enterprise', 'samsung knox', 'apple business manager'
    ];
    if (mobileProducts.some(p => combined.includes(p)) ||
      combined.includes('mobile') || combined.includes('téléphone')) {
      keywords.push('mobile');
      keywords.push('application');
      keywords.push('sideloading');
      keywords.push('credential');
      console.log(`[CTI] Detected Mobile/MDM - adding mobile attack keywords`);
    }

    // ============================================
    // API & INTEGRATION
    // ============================================
    const apiProducts = [
      'api gateway', 'apigee', 'kong', 'tyk', 'gravitee',
      'mulesoft', 'dell boomi', 'workato', 'zapier', 'make',
      'graphql', 'rest api', 'soap', 'grpc', 'websocket'
    ];
    if (apiProducts.some(p => combined.includes(p)) ||
      combined.includes('api') || combined.includes('integration')) {
      keywords.push('api');
      keywords.push('exploit public');
      keywords.push('token');
      keywords.push('authentication');
      console.log(`[CTI] Detected API/Integration - adding API attack keywords`);
    }

    // ============================================
    // DEFAULT FALLBACK
    // ============================================
    if (keywords.length === 0) {
      keywords.push('exploit');
      keywords.push('initial access');
      keywords.push('reconnaissance');
      console.log(`[CTI] No specific asset type detected - using generic attack keywords`);
    }

    return keywords;
  }

  // Extract product keywords from business asset - enhanced to include vendor, product, and version
  extractProductKeywords(asset) {
    const keywords = new Set(); // Use Set to avoid duplicates

    if (!asset) return [];

    // Add vendor if available
    if (asset.vendor && asset.vendor.trim()) {
      keywords.add(asset.vendor.trim());
      console.log(`[CTI] Extracted vendor keyword: '${asset.vendor}'`);
    }

    // Add product if available
    if (asset.product && asset.product.trim()) {
      keywords.add(asset.product.trim());
      console.log(`[CTI] Extracted product keyword: '${asset.product}'`);
    }

    // Add version-specific keyword if both product and version are available
    if (asset.product && asset.version && asset.product.trim() && asset.version.trim()) {
      const productVersion = `${asset.product.trim()} ${asset.version.trim()}`;
      keywords.add(productVersion);
      console.log(`[CTI] Extracted product+version keyword: '${productVersion}'`);
    }

    // Fallback to asset name if no vendor/product specified
    if (keywords.size === 0 && asset.name && asset.name.trim()) {
      keywords.add(asset.name.trim());
      console.log(`[CTI] No vendor/product specified, using asset name as keyword: '${asset.name}'`);
    }

    return Array.from(keywords);
  }

  // Parse technique from local MITRE API response
  parseLocalMitreTechnique(tech) {
    return {
      id: tech.external_references?.find(ref => ref.source_name === 'mitre-attack')?.external_id || tech.id,
      name: tech.name || 'Unknown Technique',
      description: tech.description || 'No description available',
      tactic: tech.kill_chain_phases?.map(phase => phase.phase_name).join(', ') || 'Unknown',
      platforms: tech.x_mitre_platforms || [],
      dataSource: tech.x_mitre_data_sources || [],
      url: this.generateMitreUrl(tech.external_references?.find(ref => ref.source_name === 'mitre-attack')?.external_id),
      source: 'Local MITRE API',
      created: tech.created,
      modified: tech.modified,
      isSubtechnique: tech.x_mitre_is_subtechnique || false
    };
  }

  // Parse ATLAS technique from local API response
  parseLocalAtlasTechnique(tech) {
    return {
      id: tech.external_references?.find(ref => ref.source_name === 'mitre-atlas')?.external_id || tech.id,
      name: tech.name || 'Unknown ATLAS Technique',
      description: tech.description || 'No description available',
      tactic: tech.kill_chain_phases?.map(phase => phase.phase_name).join(', ') || 'AI/ML Attack',
      platforms: ['AI/ML Systems'],
      dataSource: tech.x_mitre_data_sources || [],
      url: `https://atlas.mitre.org/techniques/${tech.external_references?.find(ref => ref.source_name === 'mitre-atlas')?.external_id}`,
      source: 'Local ATLAS API',
      created: tech.created,
      modified: tech.modified,
      isAtlas: true
    };
  }

  // Filter techniques by asset type using live data
  filterTechniquesByAssetType(allTechniques, assetType) {
    const assetTypeKeywords = {
      'Serveur': ['server', 'web', 'application', 'service', 'network', 'remote'],
      'Base de données': ['database', 'sql', 'data', 'credential', 'file'],
      'Application': ['application', 'web', 'client', 'execution', 'exploit'],
      'Réseau': ['network', 'remote', 'lateral', 'discovery', 'sniff'],
      'Équipement': ['system', 'endpoint', 'persistence', 'execution']
    };

    const keywords = assetTypeKeywords[assetType] || ['system', 'network'];

    return allTechniques.filter(tech => {
      const searchText = `${tech.name} ${tech.description} ${tech.tactic}`.toLowerCase();
      return keywords.some(keyword => searchText.includes(keyword));
    }).slice(0, 15);
  }

  // Map vulnerabilities to live techniques
  mapVulnerabilitiesToLiveTechniques(vulnerabilities, allTechniques) {
    const additionalTechniques = [];

    vulnerabilities.forEach(vuln => {
      const description = vuln.description?.toLowerCase() || '';

      // Find relevant techniques based on vulnerability type
      if (description.includes('sql injection')) {
        const sqlTechniques = allTechniques.filter(tech =>
          tech.name.toLowerCase().includes('exploit') ||
          tech.name.toLowerCase().includes('injection') ||
          tech.description.toLowerCase().includes('database')
        );
        additionalTechniques.push(...sqlTechniques.slice(0, 2));
      }

      if (description.includes('remote code execution')) {
        const rceTechniques = allTechniques.filter(tech =>
          tech.name.toLowerCase().includes('exploit') ||
          tech.name.toLowerCase().includes('execution') ||
          tech.tactic.toLowerCase().includes('execution')
        );
        additionalTechniques.push(...rceTechniques.slice(0, 2));
      }

      if (description.includes('authentication')) {
        const authTechniques = allTechniques.filter(tech =>
          tech.name.toLowerCase().includes('account') ||
          tech.name.toLowerCase().includes('credential') ||
          tech.tactic.toLowerCase().includes('credential')
        );
        additionalTechniques.push(...authTechniques.slice(0, 2));
      }
    });

    return additionalTechniques;
  }

  // Generate MITRE ATT&CK URL - include sub-technique part when present
  generateMitreUrl(techniqueId) {
    if (!techniqueId || !techniqueId.startsWith('T')) {
      return 'https://attack.mitre.org/techniques/';
    }

    // For sub-techniques (e.g., T1011.001), include both main and sub parts: T1011/001/
    // For main techniques (e.g., T1011), just main part: T1011/
    if (techniqueId.includes('.')) {
      const [mainId, subId] = techniqueId.split('.');
      return `https://attack.mitre.org/techniques/${mainId}/${subId}/`;
    } else {
      return `https://attack.mitre.org/techniques/${techniqueId}/`;
    }
  }

  // Get MITRE ATLAS AI/ML threats using local API
  async getAtlasThreats(asset) {
    try {
      console.log(`[CTI] Getting MITRE ATLAS threats from local API for asset: ${asset.name}`);

      // Generate AI/ML specific search keywords
      const atlasKeywords = this.generateAtlasSearchKeywords(asset);

      let allAtlasThreats = [];

      // Search for each keyword in ATLAS data
      for (const keyword of atlasKeywords) {
        console.log(`[CTI] Searching local ATLAS API with keyword: "${keyword}"`);

        const params = new URLSearchParams({
          q: keyword,
          type: 'attack-pattern' // ATLAS uses same type as MITRE ATT&CK
        });

        const response = await fetch(`${this.localMitreAtlasUrl}?${params}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          timeout: 10000
        });

        if (!response.ok) {
          console.warn(`[CTI] Local ATLAS API error for keyword "${keyword}": ${response.status}`);
          continue;
        }

        const response_data = await response.json();

        // Handle backend proxy response format
        const data = response_data.success ? response_data.data : response_data;

        console.log(`[CTI] Local ATLAS API response for "${keyword}":`, {
          success: response_data.success,
          source: response_data.source,
          resultsCount: Array.isArray(data) ? data.length : 0
        });

        // Process ATLAS techniques (data is now a direct array)
        if (Array.isArray(data) && data.length > 0) {
          const atlasTechniques = data.map(tech => this.parseLocalAtlasTechnique(tech));
          allAtlasThreats = [...allAtlasThreats, ...atlasTechniques];
        }
      }

      // Remove duplicates and limit results
      const uniqueAtlasThreats = allAtlasThreats.filter((tech, index, self) =>
        index === self.findIndex(t => t.id === tech.id)
      ).slice(0, 10); // Limit ATLAS threats

      // Log ATLAS technique diversity for debugging
      const atlasIds = uniqueAtlasThreats.map(t => t.id);
      const atlasTactics = {};
      uniqueAtlasThreats.forEach(tech => {
        const tactic = tech.tactic || 'Unknown';
        atlasTactics[tactic] = (atlasTactics[tactic] || 0) + 1;
      });

      console.log(`[CTI] Local ATLAS API returned ${uniqueAtlasThreats.length} unique threats`);
      console.log(`[CTI] ATLAS Technique IDs:`, atlasIds);
      console.log(`[CTI] ATLAS Tactic distribution:`, atlasTactics);
      console.log(`[CTI] ATLAS Sources:`, [...new Set(uniqueAtlasThreats.map(t => t.source))]);

      return uniqueAtlasThreats;

    } catch (error) {
      console.error(`[CTI] Error fetching ATLAS threats:`, error);
      // Fallback to static ATLAS threats
      return this.getStaticAtlasThreats(asset);
    }
  }

  // Generate search keywords for ATLAS based on asset vendor, product, version, and type
  generateAtlasSearchKeywords(asset) {
    const keywords = new Set();

    if (!asset) {
      console.log(`[CTI] No asset provided for ATLAS search`);
      return [];
    }

    console.log(`[CTI] Generating ATLAS search keywords for asset:`, {
      name: asset.name,
      vendor: asset.vendor,
      product: asset.product,
      version: asset.version
    });

    // Priority 1: Use vendor if available (e.g., "OpenAI", "Google", "Meta")
    if (asset.vendor && asset.vendor.trim()) {
      keywords.add(asset.vendor.trim().toLowerCase());
      console.log(`[CTI] Added vendor as ATLAS search keyword: '${asset.vendor}'`);
    }

    // Priority 2: Use product if available (e.g., "TensorFlow", "PyTorch", "GPT")
    if (asset.product && asset.product.trim()) {
      keywords.add(asset.product.trim().toLowerCase());
      console.log(`[CTI] Added product as ATLAS search keyword: '${asset.product}'`);
    }

    // Priority 3: Combine vendor + product for more specific search
    if (asset.vendor && asset.product && asset.vendor.trim() && asset.product.trim()) {
      const combinedKeyword = `${asset.vendor.trim()} ${asset.product.trim()}`.toLowerCase();
      keywords.add(combinedKeyword);
      console.log(`[CTI] Added combined vendor+product as ATLAS search keyword: '${combinedKeyword}'`);
    }

    // Priority 4: Add version-specific search if version is available
    if (asset.product && asset.version && asset.product.trim() && asset.version.trim()) {
      const productVersion = `${asset.product.trim()} ${asset.version.trim()}`.toLowerCase();
      keywords.add(productVersion);
      console.log(`[CTI] Added product+version as ATLAS search keyword: '${productVersion}'`);
    }

    // Priority 5: Use asset name as fallback
    if (asset.name && asset.name.trim()) {
      keywords.add(asset.name.trim().toLowerCase());
      console.log(`[CTI] Added asset name as ATLAS search keyword: '${asset.name}'`);
    }

    // Add general AI/ML terms as fallback/enhancement (only if we have less than 3 specific keywords)
    if (keywords.size < 3) {
      const aiKeywords = ['machine learning', 'artificial intelligence', 'model', 'neural network', 'deep learning', 'data poisoning', 'model extraction'];
      aiKeywords.forEach(k => keywords.add(k));
      console.log(`[CTI] Added general AI/ML keywords to enhance ATLAS search`);
    }

    const finalKeywords = Array.from(keywords);
    console.log(`[CTI] Generated ${finalKeywords.length} ATLAS keywords for ${asset.name}:`, finalKeywords);

    // Limit to a reasonable number to avoid too many API calls
    return finalKeywords.slice(0, 10);
  }

  // Static ATLAS threats as fallback
  getStaticAtlasThreats(asset) {
    console.log(`[CTI] Using static ATLAS threats for ${asset.name}`);

    return [
      {
        id: 'AML.T0043',
        name: 'Craft Adversarial Data',
        description: 'An adversary crafts adversarial data to evade a ML model or to cause a ML model to malfunction.',
        tactic: 'ML Attack Staging',
        platforms: ['AI/ML Systems'],
        url: 'https://atlas.mitre.org/techniques/AML.T0043',
        source: 'Static ATLAS',
        isAtlas: true
      },
      {
        id: 'AML.T0020',
        name: 'Poison Training Data',
        description: 'An adversary may poison the training data to cause a ML model to malfunction.',
        tactic: 'ML Attack Staging',
        platforms: ['AI/ML Systems'],
        url: 'https://atlas.mitre.org/techniques/AML.T0020',
        source: 'Static ATLAS',
        isAtlas: true
      },
      {
        id: 'AML.T0051',
        name: 'LLM Prompt Injection',
        description: 'An adversary may inject prompts into a large language model to cause unintended behavior.',
        tactic: 'ML Attack Staging',
        platforms: ['AI/ML Systems'],
        url: 'https://atlas.mitre.org/techniques/AML.T0051',
        source: 'Static ATLAS',
        isAtlas: true
      }
    ];
  }

  // Utility function for delays
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

export default new CTIService();
